import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n from '../i18n/config';
import { useAuth } from '../contexts/AuthContext';
import UploadService, { UploadServiceState } from '../services/uploadService';
import ApiService from '../services/apiService';
import useNavigationBlocker from '../hooks/useNavigationBlocker';
import useAudioLevel from '../hooks/useAudioLevel';
import useWakeLock from '../hooks/useWakeLock';
import {
    RECORDER_OPEN_EVENT,
    dispatchRecorderComplete,
    dispatchRecorderStatus,
    getRecorderStatusSnapshot
} from '../utils/recorderEvents';
import {
    initializeRecording,
    saveChunk,
    assembleRecordingFromChunks,
    recoverCrashedRecordings,
    removeMicrophoneRecording
} from '../utils/recorderRecovery';
import { createMixedAudioStream, MixedAudioStreamResult } from '../utils/audioMixer';
import {
    calculateRecorderChunksSize,
    createRecorderBlob,
    getRecorderFileExtension,
    isBlobCompleteForChunks,
    selectRecorderMimeType,
    RECORDER_MIME_TYPE
} from '../utils/recorderBlob';
import { assertValidFileId, debugFileId } from '../utils/fileIdDebug';
import {
    triggerAttention,
    requestNotificationPermission
} from '../utils/attentionNotification';
import '../styles/RecorderOats.css';
import { logger } from '../utils/logger';
import {
    applyPartnerRecordingTarget,
    clearPartnerRecordingTarget,
    takePartnerRecordingTarget,
    usePartnerRecordingTarget
} from '../utils/partnerRecordingContext';

interface RecorderProps {
    onRecordingComplete?: (fileId: string) => void;
    className?: string;
    variant?: 'inline' | 'floating';
    onClose?: () => void;
}

type RecordingType = 'microphone' | 'microphone+system' | 'upload';
type LiveRecordingType = 'microphone' | 'microphone+system';
const RECORDER_UI_STATE_KEY = 'notizy_recorder_ui_state_v2';

interface RecorderSessionStore {
    mediaRecorder: MediaRecorder | null;
    mediaStream: MediaStream | null;
    additionalStreams: MediaStream[];
    audioContext: AudioContext | null;
    cleanupCallbacks: Array<() => Promise<void> | void>;
    displayStreamCleanup: (() => Promise<void> | void) | null;
    recordedChunks: Blob[];
    recordingStartTime: string | null;
    preferredLiveMode: LiveRecordingType;
    activeLiveMode: LiveRecordingType;
    recordingId: string | null;
    // Formaat waarin deze sessie daadwerkelijk opneemt; verschilt per browser
    // (Safari kan geen WebM/Opus opnemen en gebruikt MP4/AAC).
    recorderMimeType: string;
    mixerControl: MixedAudioStreamResult | null;
    systemStreamLabel: string | null;
    // Notitie-concept blijft hier bewaard zodat het navigeren tussen pagina's
    // (waarbij de inline-recorder un- en remount) de tekst niet wist.
    meetingNotesDraft: string;
}

interface RecordingSetupResult {
    recorderStream: MediaStream;
    additionalStreams: MediaStream[];
    audioContext: AudioContext | null;
    cleanupCallbacks: Array<() => Promise<void> | void>;
    displayStream: MediaStream | null;
    warningMessage?: string;
    mixerControl?: MixedAudioStreamResult;
    systemStreamLabel?: string;
}

type DisplaySourceKind = 'browser-tab' | 'window' | 'screen' | 'unknown';

const createDefaultRecorderSession = (): RecorderSessionStore => ({
    mediaRecorder: null,
    mediaStream: null,
    additionalStreams: [],
    audioContext: null,
    cleanupCallbacks: [],
    displayStreamCleanup: null,
    recordedChunks: [],
    recordingStartTime: null,
    preferredLiveMode: 'microphone',
    activeLiveMode: 'microphone',
    recordingId: null,
    recorderMimeType: RECORDER_MIME_TYPE,
    mixerControl: null,
    systemStreamLabel: null,
    meetingNotesDraft: ''
});

const MICROPHONE_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 48000
};

// Interval waarmee MediaRecorder chunks aanlevert; bepaalt ook hoe vaak we naar
// IndexedDB schrijven en hoeveel audio er maximaal verloren gaat bij een crash.
const RECORDER_TIMESLICE_MS = 5000;

// Ruim voldoende voor spraak; halveert opslag en uploadtijd t.o.v. de
// browser-default (~128 kbps).
const RECORDER_AUDIO_BITS_PER_SECOND = 64000;

// Notities die je tijdens (of ter voorbereiding van) een opname maakt. Bij
// upload worden ze als ondersteunend .txt-document aan de opname gekoppeld.
const MEETING_NOTES_MAX_CHARS = 6400;
const normalizeMeetingNotes = (value: string) => value.replace(/\s+$/g, '').trim();

// Alleen de audio van de schermdeling wordt gebruikt; video is enkel nodig om de
// deelsessie (en het stoppen ervan) te detecteren. Minimale video-instellingen
// houden de capture-kosten (CPU/GPU) tijdens lange opnames laag.
// systemAudio: 'include' laat Chrome de optie "Ook systeemaudio delen" tonen bij
// het delen van het volledige scherm (Windows/ChromeOS, en macOS 14.2+ met
// Chrome 141+). Daarmee is ook het geluid van native apps (zoals de Teams
// desktop-app) op te nemen. Browsers die dit niet kennen negeren de property.
// Op platforms met scherm-systeemaudio opent de kiezer standaard op "Volledig
// scherm" (displaySurface: 'monitor', een hint — tab/venster blijft kiesbaar);
// elders blijft de tab-voorselectie zodat gebruikers bij tabaudio uitkomen.
const buildDisplayMediaConstraints = (preferScreen: boolean) => ({
    audio: true,
    systemAudio: 'include',
    video: {
        ...(preferScreen ? { displaySurface: 'monitor' } : {}),
        frameRate: { ideal: 1, max: 1 },
        width: { max: 320 },
        height: { max: 180 }
    }
});

// Chromium-only UA Client Hints; lib.dom van deze TS-versie kent userAgentData niet.
type NavigatorUAData = {
    platform?: string;
    brands?: Array<{ brand: string; version: string }>;
    getHighEntropyValues?: (hints: string[]) => Promise<{ platformVersion?: string }>;
};

// Bepaalt of de browser systeemaudio kan meesturen bij het delen van het
// volledige scherm (en niet alleen tabaudio). Windows en ChromeOS ondersteunen
// dit in elke Chromium-versie; macOS vanaf Chrome 141 op macOS 14.2+. De
// macOS-versie in de UA-string is bevroren op 10_15_7, dus de echte versie moet
// via Client Hints (platformVersion) worden opgevraagd.
const detectScreenShareSystemAudioSupport = async (): Promise<boolean> => {
    try {
        const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;
        if (!uaData?.platform) return false;

        if (uaData.platform === 'Windows' || uaData.platform === 'Chrome OS' || uaData.platform === 'Chromium OS') {
            return true;
        }

        if (uaData.platform === 'macOS') {
            const chromiumMajor = Number(uaData.brands?.find(brand => brand.brand === 'Chromium')?.version ?? 0);
            if (chromiumMajor < 141 || !uaData.getHighEntropyValues) return false;

            const { platformVersion } = await uaData.getHighEntropyValues(['platformVersion']);
            const [major = 0, minor = 0] = (platformVersion ?? '').split('.').map(Number);
            return major > 14 || (major === 14 && minor >= 2);
        }
    } catch {
        // Detectie mag de recorder nooit blokkeren; val terug op alleen tabaudio-advies.
    }
    return false;
};

// Heuristische detectie van een draadloze/Bluetooth-audio-uitvoer (oortjes,
// headset). Bij zulke apparaten kan Chrome's systeemaudio-loopback op Windows
// stilvallen zodra het standaard render-device wisselt (Chromium #1432877);
// tab-audio heeft daar geen last van. Labels zijn pas gevuld nadat er
// audiopermissie is verleend en zijn gelokaliseerd, dus dit is een hint, geen
// garantie.
const WIRELESS_OUTPUT_LABEL_PATTERN = /bluetooth|airpods?|buds|headset|headphone|koptelefoon|oortje|wireless|draadloos/i;

const detectWirelessAudioOutput = async (): Promise<boolean> => {
    try {
        const mediaDevices = navigator.mediaDevices;
        if (!mediaDevices?.enumerateDevices) return false;
        const devices = await mediaDevices.enumerateDevices();
        return devices.some(device => device.kind === 'audiooutput' && WIRELESS_OUTPUT_LABEL_PATTERN.test(device.label));
    } catch {
        return false;
    }
};

// Meet kort of er werkelijk geluid binnenkomt op een (systeem)audiostream.
// Resolvet zodra het signaal de drempel overschrijdt (meestal <300ms) of na
// `durationMs` stilte. Gebruikt om bij draadloze oortjes een live-maar-stille
// loopback-track te herkennen vóórdat we een stille opname starten.
const SYSTEM_SIGNAL_BYTE_THRESHOLD = 6; // zelfde orde als startSourceAudioWatch/detectActiveAudio
const SYSTEM_SIGNAL_LEVEL_THRESHOLD = 0.04; // useAudioLevel kwantiseert in stappen van 0,05
const SYSTEM_SILENCE_VERIFICATION_MESSAGE = i18n.t('recorder.warnings.silenceVerification');
const DEVICE_CHANGE_SYSTEM_AUDIO_MESSAGE = i18n.t('recorder.warnings.deviceChangeSystemAudio');

const probeStreamHasSignal = (stream: MediaStream, durationMs = 1500): Promise<boolean> =>
    new Promise(resolve => {
        const track = stream.getAudioTracks()[0];
        if (!track || track.readyState !== 'live') {
            resolve(false);
            return;
        }

        let audioContext: AudioContext | null = null;
        try {
            audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const ctx = audioContext;
            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            if (ctx.state === 'suspended') {
                void ctx.resume().catch(() => undefined);
            }

            const data = new Uint8Array(analyser.frequencyBinCount);
            const startedAt = Date.now();

            const finish = (result: boolean) => {
                clearInterval(intervalId);
                try {
                    source.disconnect();
                } catch {
                    // Bron was al losgekoppeld.
                }
                void ctx.close().catch(() => undefined);
                resolve(result);
            };

            const intervalId = setInterval(() => {
                analyser.getByteFrequencyData(data);
                const average = data.reduce((sum, value) => sum + value, 0) / data.length;
                if (average > SYSTEM_SIGNAL_BYTE_THRESHOLD) {
                    finish(true);
                } else if (Date.now() - startedAt >= durationMs) {
                    finish(false);
                }
            }, 150);
        } catch {
            void audioContext?.close().catch(() => undefined);
            resolve(false);
        }
    });

const formatSystemStreamLabel = (label: string): string => {
    if (!label) return i18n.t('recorder.systemAudioDefaultLabel');
    // Heuristic: raw IDs often look like "window:1234:0" or "screen:0"
    if (label.toLowerCase().includes('window:') || label.toLowerCase().includes('screen:')) {
        return i18n.t('recorder.sharedScreenWindowLabel');
    }
    return label;
};

const inferDisplaySourceKind = (displayStream: MediaStream | null): DisplaySourceKind => {
    const videoTrack = displayStream?.getVideoTracks()[0];
    if (!videoTrack) {
        return 'unknown';
    }

    const settings = videoTrack.getSettings?.() as MediaTrackSettings & { displaySurface?: string };
    const displaySurface = settings?.displaySurface;

    if (displaySurface === 'browser') return 'browser-tab';
    if (displaySurface === 'window') return 'window';
    if (displaySurface === 'monitor') return 'screen';

    const lowerLabel = (videoTrack.label || '').toLowerCase();
    if (lowerLabel.includes('window:') || lowerLabel.includes('venster')) return 'window';
    if (lowerLabel.includes('screen:') || lowerLabel.includes('monitor')) return 'screen';
    if (lowerLabel.includes('tab') || lowerLabel.includes('teams.microsoft.com') || lowerLabel.includes('zoom.us')) return 'browser-tab';

    return 'unknown';
};

const buildMissingSystemAudioMessage = (displayStream: MediaStream | null): string => {
    const sourceKind = inferDisplaySourceKind(displayStream);
    const lowerLabel = (displayStream?.getVideoTracks()[0]?.label || '').toLowerCase();
    const teamsDesktopLikely = lowerLabel.includes('teams') && sourceKind !== 'browser-tab';

    if (teamsDesktopLikely || sourceKind === 'window' || sourceKind === 'screen') {
        return i18n.t('recorder.missingSystemAudio.notReceived');
    }

    return i18n.t('recorder.missingSystemAudio.notDetected');
};

const buildDisplaySelectionWarning = (displayStream: MediaStream | null, screenAudioSupported: boolean): string | null => {
    // Komt er al audio mee (bijv. systeemaudio bij een gedeeld scherm), dan is
    // er geen reden om voor de bronkeuze te waarschuwen.
    if (displayStream && displayStream.getAudioTracks().some(track => track.readyState === 'live')) {
        return null;
    }

    const sourceKind = inferDisplaySourceKind(displayStream);
    const lowerLabel = (displayStream?.getVideoTracks()[0]?.label || '').toLowerCase();
    const teamsDesktopLikely = lowerLabel.includes('teams') && sourceKind !== 'browser-tab';

    if (teamsDesktopLikely) {
        if (screenAudioSupported) {
            return i18n.t('recorder.warnings.teamsWindowNoAudioScreenAudio');
        }
        return i18n.t('recorder.warnings.teamsWindowNoAudio');
    }

    if (sourceKind === 'window' || sourceKind === 'screen') {
        if (screenAudioSupported) {
            return i18n.t('recorder.warnings.windowScreenNoAudioScreenAudio');
        }
        return i18n.t('recorder.warnings.windowScreenNoAudio');
    }

    return null;
};

const ensureRecorderSession = (): RecorderSessionStore => {
    if (typeof window === 'undefined') {
        return createDefaultRecorderSession();
    }

    if (!window.__notizyRecorderSession) {
        window.__notizyRecorderSession = createDefaultRecorderSession();
    }

    const session: RecorderSessionStore = window.__notizyRecorderSession;
    if (!Array.isArray(session.additionalStreams)) {
        session.additionalStreams = [];
    }
    if (!Array.isArray(session.cleanupCallbacks)) {
        session.cleanupCallbacks = [];
    }
    if (typeof session.displayStreamCleanup !== 'function') {
        session.displayStreamCleanup = null;
    }
    if (!Array.isArray(session.recordedChunks)) {
        session.recordedChunks = [];
    }
    if (!session.preferredLiveMode) {
        session.preferredLiveMode = 'microphone';
    }
    if (!session.activeLiveMode) {
        session.activeLiveMode = session.preferredLiveMode;
    }
    if (typeof session.meetingNotesDraft !== 'string') {
        session.meetingNotesDraft = '';
    }

    return session;
};

declare global {
    interface Window {
        __notizyRecorderSession?: RecorderSessionStore;
    }
}

const AudioVisualizer: React.FC<{ stream: MediaStream | null; color?: 'white' | 'black' }> = ({ stream, color = 'white' }) => {
    const level = useAudioLevel(stream);

    return (
        <div className="flex items-center gap-1 h-6">
            {Array.from({ length: 20 }).map((_, i) => {
                const threshold = i / 20;
                const isLit = level > threshold;

                let barClass = '';
                if (color === 'white') {
                    barClass = isLit
                        ? 'bg-gradient-to-t from-white to-white h-full'
                        : 'bg-white/30 h-1/3';
                } else {
                    barClass = isLit
                        ? 'bg-gray-900 h-full'
                        : 'bg-gray-300 h-1/3';
                }

                return (
                    <div
                        key={i}
                        className={`w-1.5 rounded-full transition-all duration-75 ${barClass}`}
                        style={{
                            opacity: isLit ? 1 : 0.5
                        }}
                    />
                );
            })}
        </div>
    );
};

// Waveform-meter zoals in de desktop-app: een rij DOM-balkjes die elke 200ms
// een nieuw audioniveau toevoegen. Hoogte per balk: 6 + level * 22 px.
const WAVEFORM_BARS = 200;
const WAVEFORM_SAMPLE_INTERVAL = 200;
const METER_BAR_SLOT = 5; // px per balk inclusief gap

const OatsMeter: React.FC<{ stream: MediaStream | null; isPaused: boolean }> = ({ stream, isPaused }) => {
    const level = useAudioLevel(stream, !isPaused);
    const levelRef = useRef(0);
    levelRef.current = level;

    const [waveformData, setWaveformData] = useState<number[]>([]);
    const [barCount, setBarCount] = useState(12);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (isPaused) {
            return undefined;
        }

        const interval = setInterval(() => {
            setWaveformData(prev => {
                const next = [...prev, Math.min(1, levelRef.current * 1.5)];
                return next.length > WAVEFORM_BARS ? next.slice(next.length - WAVEFORM_BARS) : next;
            });
        }, WAVEFORM_SAMPLE_INTERVAL);

        return () => clearInterval(interval);
    }, [isPaused]);

    useEffect(() => {
        const element = containerRef.current;
        if (!element) {
            return undefined;
        }

        const measure = () => {
            setBarCount(Math.max(12, Math.floor(element.clientWidth / METER_BAR_SLOT)));
        };

        measure();

        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    const recent = waveformData.slice(-barCount);
    const bars = recent.length >= barCount
        ? recent
        : [...Array.from({ length: barCount - recent.length }, () => 0), ...recent];

    return (
        <div className={`recorder-oats-meter${isPaused ? ' paused' : ''}`} aria-hidden="true" ref={containerRef}>
            {bars.map((bar, index) => (
                <span
                    key={index}
                    className="recorder-oats-meter-bar"
                    style={{ height: `${6 + bar * 22}px` }}
                />
            ))}
        </div>
    );
};

const Recorder: React.FC<RecorderProps> = ({ onRecordingComplete, className = '', variant = 'floating', onClose }) => {
    const { t } = useTranslation();
    const recorderSessionRef = useRef<RecorderSessionStore>(ensureRecorderSession());
    const initialStatus = React.useMemo(() => getRecorderStatusSnapshot(), []);
    const [isRecording, setIsRecording] = useState(initialStatus?.isRecording ?? false);
    const [isPaused, setIsPaused] = useState(initialStatus?.isPaused ?? false);

    // Voorkom dat het systeem tijdens een (gepauzeerde) opname door
    // inactiviteit in slaapstand gaat; dat zou de opname afbreken.
    useWakeLock(isRecording || isPaused);
    const [recordingTime, setRecordingTime] = useState(initialStatus?.recordingTime ?? 0);
    const [errorMessage, setErrorMessage] = useState('');
    const [, setIsExtensionAvailable] = useState(false);
    const [recordingType, setRecordingType] = useState<RecordingType>('microphone');
    const [preferredLiveMode, setPreferredLiveMode] = useState<LiveRecordingType>(recorderSessionRef.current.preferredLiveMode ?? 'microphone');
    const [activeLiveMode, setActiveLiveMode] = useState<LiveRecordingType>(recorderSessionRef.current.activeLiveMode ?? preferredLiveMode);
    const [systemAudioSupport, setSystemAudioSupport] = useState<{ available: boolean; reason?: string; screenAudio?: boolean }>({ available: false });
    const [wirelessOutputDetected, setWirelessOutputDetected] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [recordingStartTime, setRecordingStartTime] = useState<string | null>(recorderSessionRef.current.recordingStartTime);
    const [isLiveModeDialogOpen, setIsLiveModeDialogOpen] = useState(false);
    const [isStartingRecording, setIsStartingRecording] = useState(false);
    const [isProcessingRecording, setIsProcessingRecording] = useState(false);
    const [isChoosingMode, setIsChoosingMode] = useState(false);
    const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
    const [isConfirmingRecoveryDelete, setIsConfirmingRecoveryDelete] = useState(false);
    const [dismissedRecoveryId, setDismissedRecoveryId] = useState<string | null>(null);
    const [isRecorderInView, setIsRecorderInView] = useState(false);
    const recorderRootRef = useRef<HTMLElement | null>(null);
    const confirmDiscardTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const confirmRecoveryDeleteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const sourceWatchCleanupRef = useRef<(() => void) | null>(null);
    const isDiscardingRef = useRef(false);

    // Wizard State
    const [wizardStep, setWizardStep] = useState<'mode-selection' | 'instruction' | 'verification'>('mode-selection');
    const [previewStreams, setPreviewStreams] = useState<{ mic?: MediaStream; system?: MediaStream } | null>(null);
    const [verificationError, setVerificationError] = useState<string | null>(null);
    const [verificationWarning, setVerificationWarning] = useState<string | null>(null);
    const [recordingSourceWarning, setRecordingSourceWarning] = useState<string | null>(null);
    const previewSelectionWarning = buildDisplaySelectionWarning(previewStreams?.system || null, !!systemAudioSupport.screenAudio);

    // #3: live meting van de systeem-preview tijdens de verificatiestap, zodat
    // we kunnen waarschuwen wanneer er een audiotrack is maar geen signaal.
    const systemPreviewLevel = useAudioLevel(
        wizardStep === 'verification' ? (previewStreams?.system ?? null) : null
    );
    const systemPreviewLevelRef = useRef(0);
    systemPreviewLevelRef.current = systemPreviewLevel;
    const previewStreamsRef = useRef(previewStreams);
    previewStreamsRef.current = previewStreams;

    const uploadService = React.useMemo(() => UploadService.getInstance(), []);
    const apiService = React.useMemo(() => new ApiService(), []);
    const [uploadState, setUploadState] = useState<UploadServiceState>(() => uploadService.getState());
    const [extensionStatusMessage, setExtensionStatusMessage] = useState('');
    const [userStatusMessage, setUserStatusMessage] = useState('');
    const [extensionUploading, setExtensionUploading] = useState(initialStatus?.isUploading ?? false);
    const [extensionUploadProgress, setExtensionUploadProgress] = useState(initialStatus?.uploadProgress ?? 0);
    const [extensionUploadComplete, setExtensionUploadComplete] = useState(false);

    // Notities bij de opname. Een ref houdt de meest recente tekst vast zodat de
    // (async) upload-afhandeling niet op een verouderde closure leunt.
    // Alleen de inline-variant toont én beheert de notities; de floating-variant
    // laat de gedeelde sessie-tekst met rust om een clobber te voorkomen.
    const [meetingNotesDraft, setMeetingNotesDraft] = useState(
        () => (variant === 'inline' ? (recorderSessionRef.current?.meetingNotesDraft ?? '') : '')
    );
    const [isNotesExpanded, setIsNotesExpanded] = useState(
        () => variant === 'inline' && !!recorderSessionRef.current?.meetingNotesDraft
    );
    const meetingNotesDraftRef = useRef('');
    meetingNotesDraftRef.current = meetingNotesDraft;
    // Spiegel het concept naar de gedeelde sessie zodat navigeren tussen pagina's
    // (un-/remount van de inline-recorder) de tekst behoudt.
    if (variant === 'inline' && recorderSessionRef.current) {
        recorderSessionRef.current.meetingNotesDraft = meetingNotesDraft;
    }
    const notesTextareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Zodra een opname loopt klappen we het notitieveld open, zodat de gebruiker
    // zich uitgenodigd voelt er meteen iets in te zetten. Daarna mag hij het zelf
    // weer dichtklappen.
    useEffect(() => {
        if (isRecording) {
            setIsNotesExpanded(true);
        }
    }, [isRecording]);

    const resumeRequestedRef = useRef(false);
    // Houdt per pending-id bij of er al automatisch een upload-hervatting is
    // geprobeerd, zodat een mislukte poging niet meteen opnieuw vuurt.
    const autoResumeMicAttemptedIdRef = useRef<string | null>(null);
    const autoResumeFileAttemptedIdRef = useRef<string | null>(null);
    const extensionPendingFileIdRef = useRef<string | null>(null);
    const extensionResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isSwitchingDisplaySourceRef = useRef(false);

    const { user } = useAuth();
    // Afspraak uit Start Notizy bij een partner; gaat mee met de eerstvolgende upload
    const partnerRecordingTarget = usePartnerRecordingTarget();
    const location = useLocation();
    const navigate = useNavigate();
    const [mediaRecorderInstance, setMediaRecorderInstance] = useState<MediaRecorder | null>(() => recorderSessionRef.current?.mediaRecorder ?? null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(recorderSessionRef.current?.mediaRecorder ?? null);
    const recordedChunksRef = useRef<Blob[]>(recorderSessionRef.current?.recordedChunks ?? []);
    const currentRecordingIdRef = useRef<string | null>(recorderSessionRef.current?.recordingId ?? null);
    const recorderMimeTypeRef = useRef<string>(recorderSessionRef.current?.recorderMimeType || RECORDER_MIME_TYPE);
    const pendingChunkWritePromisesRef = useRef<Set<Promise<void>>>(new Set());
    const lastChunkPersistenceErrorRef = useRef<Error | null>(null);
    recorderSessionRef.current.recordedChunks = recordedChunksRef.current;

    const extensionActive = Boolean(extensionPendingFileIdRef.current);
    const isUploading = extensionActive ? extensionUploading : uploadState.isUploading;
    const uploadProgress = extensionActive ? extensionUploadProgress : uploadState.progress;
    const isUploadComplete = extensionActive ? extensionUploadComplete : uploadState.isComplete;
    const pendingMicrophoneRecovery = uploadState.pendingMicrophoneRecording;
    const pendingFileRecovery = uploadState.pendingFileUpload;
    const hasPendingRecovery = Boolean(pendingMicrophoneRecovery || pendingFileRecovery);
    const statusMessage = userStatusMessage || extensionStatusMessage || uploadState.statusMessage;
    const uploadErrorMessage = uploadState.error ?? '';
    const visibleErrorMessage = errorMessage || uploadErrorMessage;

    const isFloatingVariant = variant === 'floating';
    // De kaart wordt alleen inline (op /recordings) getoond; de floating
    // variant rendert uitsluitend de zwevende indicator-pill.
    const canShowContainer = variant === 'inline';
    const isBusy = isRecording || (isUploading && !isUploadComplete) || isPaused || isProcessingRecording;
    const navigationWarningMessage = isUploading
        ? t('recorder.navigation.uploadInProgress')
        : isProcessingRecording
            ? t('recorder.navigation.processing')
        : isPaused
            ? t('recorder.navigation.paused')
            : t('recorder.navigation.recording');

    useNavigationBlocker(isBusy, navigationWarningMessage);

    const updateRecordingStartTime = React.useCallback((value: string | null) => {
        setRecordingStartTime(value);
        if (recorderSessionRef.current) {
            recorderSessionRef.current.recordingStartTime = value;
        }
    }, []);

    const waitForPendingChunkWrites = React.useCallback(async () => {
        const pendingWrites = Array.from(pendingChunkWritePromisesRef.current);
        if (pendingWrites.length === 0) {
            return;
        }

        await Promise.all(pendingWrites);
    }, []);

    const persistRecordedChunk = React.useCallback((recordingId: string, chunk: Blob, chunkIndex: number) => {
        const writePromise = saveChunk(recordingId, chunk, chunkIndex)
            .catch((error) => {
                const normalizedError = error instanceof Error
                    ? error
                    : new Error(t('recorder.errors.chunkSaveFailed'));
                lastChunkPersistenceErrorRef.current = normalizedError;
                logger.warn('Failed to save chunk to DB:', normalizedError);
            });

        pendingChunkWritePromisesRef.current.add(writePromise);
        void writePromise.finally(() => {
            pendingChunkWritePromisesRef.current.delete(writePromise);
        });
    }, [t]);

    const resetExtensionState = React.useCallback(() => {
        setExtensionUploading(false);
        setExtensionUploadProgress(0);
        setExtensionUploadComplete(false);
        setExtensionStatusMessage('');
        if (extensionResetTimeoutRef.current) {
            clearTimeout(extensionResetTimeoutRef.current);
            extensionResetTimeoutRef.current = null;
        }
    }, []);

    const scheduleExtensionStateReset = React.useCallback((delay = 800) => {
        if (extensionResetTimeoutRef.current) {
            clearTimeout(extensionResetTimeoutRef.current);
        }
        extensionResetTimeoutRef.current = setTimeout(() => {
            extensionResetTimeoutRef.current = null;
            resetExtensionState();
        }, delay);
    }, [resetExtensionState]);

    useEffect(() => {
        const unsubscribe = uploadService.subscribe((nextState) => {
            setUploadState(nextState);
        });
        return unsubscribe;
    }, [uploadService]);

    useEffect(() => {
        const session = recorderSessionRef.current;
        if (!session) {
            return;
        }

        recordedChunksRef.current = session.recordedChunks;
        session.recordedChunks = recordedChunksRef.current;
        recorderMimeTypeRef.current = session.recorderMimeType || RECORDER_MIME_TYPE;

        if (session.preferredLiveMode) {
            setPreferredLiveMode(session.preferredLiveMode);
        }
        if (session.activeLiveMode) {
            setActiveLiveMode(session.activeLiveMode);
        }

        if (session.mediaRecorder) {
            mediaRecorderRef.current = session.mediaRecorder;
            setMediaRecorderInstance(session.mediaRecorder);
            if (session.mediaRecorder.state === 'recording') {
                setIsRecording(true);
                setIsPaused(false);
            } else if (session.mediaRecorder.state === 'paused') {
                setIsRecording(false);
                setIsPaused(true);
            }
        }

        if (session.recordingStartTime) {
            const startMillis = new Date(session.recordingStartTime).getTime();
            if (!Number.isNaN(startMillis)) {
                const elapsed = Math.max(0, Math.floor((Date.now() - startMillis) / 1000));
                setRecordingTime(prev => (prev > 0 ? prev : elapsed));
            }
            updateRecordingStartTime(session.recordingStartTime);
        }

        // Loopt er (na navigatie) nog een opname met systeemaudio, herstart dan
        // de audiobewaking van de gedeelde bron in deze component-instantie.
        if (session.mediaRecorder && session.mediaRecorder.state !== 'inactive') {
            const activeDisplayStream = session.additionalStreams.find(stream => stream.getVideoTracks().length > 0) ?? null;
            if (activeDisplayStream) {
                startSourceAudioWatch(activeDisplayStream);
            }
        }

        // Check for crashed recordings on mount; de opname die in deze sessie
        // nog loopt (over navigaties heen) telt niet als crash.
        const checkCrashedRecordings = async () => {
            const recovered = await recoverCrashedRecordings(recorderSessionRef.current.recordingId);
            if (recovered.length > 0) {
                // Herstelde opnames staan nu in de IndexedDB pending-lijst; ververs de
                // uploadService-state zodat de "Upload hervatten"-knop daadwerkelijk verschijnt.
                await uploadService.refreshRecoveryState();
                setUserStatusMessage(t('recorder.errors.recoveredRecordings', { count: recovered.length }));
                // Force expand to show the message
                if (!isFloatingVariant || location.pathname === '/recordings') {
                    setIsExpanded(true);
                }
            }
        };
        checkCrashedRecordings();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleOpen = () => {
            if (isFloatingVariant && location.pathname !== '/recordings') {
                return;
            }
            setIsExpanded(true);
        };

        window.addEventListener(RECORDER_OPEN_EVENT, handleOpen);
        return () => {
            window.removeEventListener(RECORDER_OPEN_EVENT, handleOpen);
        };
    }, [isFloatingVariant, location.pathname]);

    useEffect(() => {
        if (!isFloatingVariant) {
            // Inline variant is altijd expanded
            setIsExpanded(true);
            return;
        }

        if (location.pathname !== '/recordings') {
            setIsExpanded(false);
        }
    }, [isFloatingVariant, location.pathname]);

    // Debug useEffect specifiek voor isUploading changes
    useEffect(() => {

    }, [isUploading]);

    useEffect(() => {

        dispatchRecorderStatus({
            isRecording,
            isPaused,
            isUploading,
            uploadProgress,
            recordingTime,
            statusMessage
        });
    }, [isRecording, isPaused, isUploading, uploadProgress, recordingTime, statusMessage]);

    // Auto-save recording every 10 seconds if we have an ID
    useEffect(() => {
        if (!isFloatingVariant) {
            try {
                const storedStateRaw = localStorage.getItem(RECORDER_UI_STATE_KEY);
                if (storedStateRaw) {
                    const storedState = JSON.parse(storedStateRaw) as Partial<{
                        recordingType: RecordingType;
                        isExpanded: boolean;
                        preferredLiveMode: LiveRecordingType;
                    }>;

                    if (storedState.recordingType) {
                        setRecordingType(storedState.recordingType === 'microphone+system' ? 'microphone' : storedState.recordingType);
                    }

                    if (typeof storedState.isExpanded === 'boolean') {
                        setIsExpanded(storedState.isExpanded);
                    }

                    if (storedState.preferredLiveMode) {
                        setPreferredLiveMode(storedState.preferredLiveMode);
                    }
                }
            } catch (error) {
                logger.warn('Kon recorder UI state niet laden:', error);
            }
        } else {
            setIsExpanded(false);
        }
    }, [isFloatingVariant]);

    useEffect(() => {
        if (hasPendingRecovery && (!isFloatingVariant || location.pathname === '/recordings')) {
            setIsExpanded(true);
        }
    }, [hasPendingRecovery, isFloatingVariant, location.pathname]);

    useEffect(() => {
        if (isFloatingVariant) {
            return;
        }

        try {
            localStorage.setItem(RECORDER_UI_STATE_KEY, JSON.stringify({
                recordingType,
                isExpanded,
                preferredLiveMode
            }));
        } catch (error) {
            logger.warn('Kon recorder UI state niet opslaan:', error);
        }
    }, [isFloatingVariant, preferredLiveMode, recordingType, isExpanded]);

    // Proactief permissie vragen zodra de recorder openklapt
    useEffect(() => {
        if (isExpanded && !isRecording && !isPaused && !isUploading) {
            // Check of we al permissie hebben (dit is een benadering, we proberen gewoon de stream te krijgen)
            // We doen dit alleen als we nog niet aan het opnemen zijn
            const requestPermission = async () => {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    // Als het lukt, direct weer stoppen. We wilden alleen de permissie prompt triggeren (of checken)
                    stream.getTracks().forEach(track => track.stop());
                } catch (error) {
                    // Negeren, de gebruiker kan het later alsnog proberen via de start knop,
                    // of heeft het geweigerd. We willen hier geen error tonen.
                    logger.debug('Proactive permission request failed or denied:', error);
                }
            };
            requestPermission();
        }
    }, [isExpanded, isRecording, isPaused, isUploading]);

    useEffect(() => {
        // Ook tijdens een pauze loopt de opname nog: die mag niet verloren gaan
        if (!(isRecording || isPaused || isUploading)) {
            return;
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isRecording, isPaused, isUploading]);

    const checkExtensionAvailability = () => {
        // Check of Notizy Chrome extensie geïnstalleerd is
        const extensionCheckScript = document.createElement('script');
        extensionCheckScript.textContent = `
      // Signal naar de extensie dat Notizy web app klaar is
      window.postMessage({ type: 'NOTIZY_WEB_APP_READY' }, '*');
      
      // Check voor extensie response
      setTimeout(() => {
        window.postMessage({ type: 'NOTIZY_EXTENSION_CHECK' }, '*');
      }, 100);
    `;

        document.head.appendChild(extensionCheckScript);
        document.head.removeChild(extensionCheckScript);

        // Luister naar extensie response
        const checkResponse = (event: MessageEvent) => {
            if (event.source !== window) return;

            if (event.data.type === 'NOTIZY_EXTENSION_AVAILABLE') {
                setIsExtensionAvailable(true);
                window.removeEventListener('message', checkResponse);
                window.postMessage({ type: 'NOTIZY_EXTENSION_STATUS_REQUEST' }, '*');
            }
        };

        window.addEventListener('message', checkResponse);

        // Fallback: na 2 seconden geen response, extensie niet beschikbaar
        setTimeout(() => {
            window.removeEventListener('message', checkResponse);
            // Laat gebruiker zelf recordingType kiezen, switch niet automatisch
        }, 2000);
    };

    useEffect(() => {
        if (typeof navigator === 'undefined') {
            setSystemAudioSupport({ available: false, reason: t('recorder.systemAudioSupport.browserContextUnavailable') });
            return;
        }

        const mediaDevices = navigator.mediaDevices;
        if (!mediaDevices || typeof mediaDevices.getDisplayMedia !== 'function') {
            setSystemAudioSupport({ available: false, reason: t('recorder.systemAudioSupport.noScreenShareAudio') });
            return;
        }

        const userAgent = navigator.userAgent.toLowerCase();
        const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('crios') && !userAgent.includes('fxios');
        const isFirefox = userAgent.includes('firefox');

        if (isSafari) {
            setSystemAudioSupport({ available: false, reason: t('recorder.systemAudioSupport.safariUnsupported') });
            return;
        }

        if (isFirefox) {
            setSystemAudioSupport({ available: false, reason: t('recorder.systemAudioSupport.firefoxUnsupported') });
            return;
        }

        setSystemAudioSupport({ available: true });

        let cancelled = false;
        detectScreenShareSystemAudioSupport().then(screenAudio => {
            if (!cancelled && screenAudio) {
                setSystemAudioSupport(prev => ({ ...prev, screenAudio: true }));
            }
        });
        return () => {
            cancelled = true;
        };
    }, [t]);

    useEffect(() => {
        if (!systemAudioSupport.available && recordingType === 'microphone+system') {
            setRecordingType('microphone');
        }
    }, [recordingType, systemAudioSupport.available]);

    useEffect(() => {
        if (!systemAudioSupport.available && preferredLiveMode === 'microphone+system') {
            setPreferredLiveMode('microphone');
        }
    }, [preferredLiveMode, systemAudioSupport.available]);

    useEffect(() => {
        if (recorderSessionRef.current) {
            recorderSessionRef.current.preferredLiveMode = preferredLiveMode;
        }
    }, [preferredLiveMode]);

    useEffect(() => {
        if (recorderSessionRef.current) {
            recorderSessionRef.current.activeLiveMode = activeLiveMode;
        }
    }, [activeLiveMode]);

    const startTimer = React.useCallback((initialSeconds?: number) => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        if (typeof initialSeconds === 'number' && !Number.isNaN(initialSeconds)) {
            setRecordingTime(initialSeconds);
        }

        timerRef.current = setInterval(() => {
            setRecordingTime(prev => prev + 1);
        }, 1000);
    }, []);

    const stopTimer = React.useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!isRecording) {
            return undefined;
        }

        const baseSeconds = (() => {
            if (recordingStartTime) {
                const startMillis = new Date(recordingStartTime).getTime();
                if (!Number.isNaN(startMillis)) {
                    return Math.max(0, Math.floor((Date.now() - startMillis) / 1000));
                }
            }
            return recordingTime;
        })();

        if (typeof baseSeconds === 'number' && !Number.isNaN(baseSeconds)) {
            startTimer(baseSeconds);
        } else {
            startTimer();
        }

        return () => {
            stopTimer();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRecording, recordingStartTime, startTimer, stopTimer]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const syncExtensionStatus = React.useCallback((status: any) => {
        if (!status) return;

        if (status.isRecording) {
            const startTime = status.recordingStartTime ? new Date(status.recordingStartTime) : new Date();
            const elapsedSeconds = Math.max(0, Math.floor((Date.now() - startTime.getTime()) / 1000));
            updateRecordingStartTime(startTime.toISOString());
            setIsRecording(true);
            startTimer(elapsedSeconds);
        } else {
            setIsRecording(false);
            stopTimer();
        }

        if (status.pendingUpload) {
            const pending = status.pendingUpload;
            extensionPendingFileIdRef.current = pending.fileId;
            const progressValue = typeof pending.progress === 'number'
                ? Math.max(0, Math.min(100, Math.round(pending.progress)))
                : 0;

            setExtensionUploadProgress(progressValue);
            if (progressValue === 100 && pending.state !== 'waiting' && pending.state !== 'queued') {
                setExtensionUploadComplete(true);
                setExtensionUploading(false);
                extensionPendingFileIdRef.current = null;
                resumeRequestedRef.current = false;
                scheduleExtensionStateReset();
            } else {
                if (extensionUploadComplete || extensionResetTimeoutRef.current) {
                    return;
                }

                const isWaiting = pending.state === 'waiting' || pending.state === 'queued';
                const isUploadingState = pending.state === 'uploading' || isWaiting;
                setExtensionUploading(isUploadingState);

                if (isWaiting && !resumeRequestedRef.current) {
                    resumeRequestedRef.current = true;
                    setExtensionStatusMessage(t('recorder.errors.resumeUploadWaiting'));
                    window.postMessage({
                        type: 'NOTIZY_RESUME_UPLOAD',
                        data: { fileId: pending.fileId }
                    }, '*');
                } else if (pending.state === 'uploading') {
                    setExtensionStatusMessage(t('recorder.errors.extensionUploadActive', { progress: progressValue }));
                }
            }
        } else {
            extensionPendingFileIdRef.current = null;
            resumeRequestedRef.current = false;
            if (!uploadState.isUploading && !pendingMicrophoneRecovery && !pendingFileRecovery) {
                setExtensionStatusMessage('');
            }
        }
    }, [
        extensionUploadComplete,
        pendingFileRecovery,
        pendingMicrophoneRecovery,
        scheduleExtensionStateReset,
        startTimer,
        stopTimer,
        t,
        updateRecordingStartTime,
        uploadState.isUploading
    ]);

    useEffect(() => {
        checkExtensionAvailability();

        const handleExtensionMessage = (event: MessageEvent) => {
            if (event.source !== window) return;

            const { type, data } = event.data;

            switch (type) {
                case 'NOTIZY_RECORDING_STARTED':
                    setIsRecording(true);
                    setRecordingTime(0);
                    updateRecordingStartTime(new Date().toISOString());
                    startTimer();
                    break;

                case 'NOTIZY_RECORDING_STOPPED':
                    setIsRecording(false);
                    stopTimer();
                    break;

                case 'NOTIZY_EXTENSION_STATUS':
                    syncExtensionStatus(data);
                    break;

                case 'NOTIZY_UPLOAD_STARTED':
                    setExtensionUploading(true);
                    setExtensionUploadProgress(0);
                    setExtensionUploadComplete(false);
                    setExtensionStatusMessage(t('recorder.errors.extensionUploadStarted'));
                    if (extensionResetTimeoutRef.current) {
                        clearTimeout(extensionResetTimeoutRef.current);
                        extensionResetTimeoutRef.current = null;
                    }
                    break;

                case 'NOTIZY_UPLOAD_PROGRESS':
                    setExtensionUploadProgress(data.progress);
                    setExtensionStatusMessage(t('recorder.errors.extensionUploadActive', { progress: Math.round(data.progress) }));
                    break;

                case 'NOTIZY_UPLOAD_COMPLETE':
                    setExtensionUploadProgress(100);
                    if (onRecordingComplete && data.fileId) {
                        onRecordingComplete(data.fileId);
                    }
                    if (data.fileId) {
                        dispatchRecorderComplete(data.fileId);
                    }
                    setExtensionUploadComplete(true);
                    setExtensionUploading(false);
                    extensionPendingFileIdRef.current = null;
                    resumeRequestedRef.current = false;
                    scheduleExtensionStateReset();
                    break;

                case 'NOTIZY_UPLOAD_ERROR':
                    setExtensionUploading(false);
                    setExtensionUploadProgress(0);
                    setExtensionUploadComplete(false);
                    setErrorMessage(data.error || t('recorder.errors.uploadErrorOccurred'));
                    resumeRequestedRef.current = false;
                    setExtensionStatusMessage('');
                    break;

                case 'NOTIZY_UPLOAD_RESUMED':
                    setExtensionStatusMessage(t('recorder.errors.extensionUploadResumed'));
                    setExtensionUploading(true);
                    resumeRequestedRef.current = true;
                    break;

                case 'NOTIZY_UPLOAD_WAITING':
                    setExtensionStatusMessage(data?.message || t('recorder.errors.extensionUploadWaiting'));
                    setExtensionUploading(true);
                    resumeRequestedRef.current = false;
                    break;
            }
        };

        window.addEventListener('message', handleExtensionMessage);

        return () => {
            window.removeEventListener('message', handleExtensionMessage);
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            if (extensionResetTimeoutRef.current) {
                clearTimeout(extensionResetTimeoutRef.current);
                extensionResetTimeoutRef.current = null;
            }
        };
    }, [onRecordingComplete, scheduleExtensionStateReset, syncExtensionStatus, startTimer, stopTimer, t, updateRecordingStartTime]);

    const stopStreamTracks = (stream?: MediaStream | null) => {
        if (!stream) {
            return;
        }

        stream.getTracks().forEach(track => {
            try {
                track.stop();
            } catch (trackError) {
                logger.warn('Kon MediaStreamTrack niet stoppen:', trackError);
            }
        });
    };

    const getCurrentDisplayStream = (session?: RecorderSessionStore | null): MediaStream | null => {
        if (!session) {
            return null;
        }

        return session.additionalStreams.find(stream => stream.getVideoTracks().length > 0) ?? null;
    };

    const registerDisplayStreamCleanup = (displayStream: MediaStream) => {
        const audioTracks = displayStream.getAudioTracks();
        const videoTracks = displayStream.getVideoTracks();
        let displayStopHandled = false;

        const handleDisplayEnded = () => {
            const session = recorderSessionRef.current;
            const currentDisplayStream = getCurrentDisplayStream(session);

            if (isSwitchingDisplaySourceRef.current) {
                return;
            }

            if (!currentDisplayStream || currentDisplayStream !== displayStream) {
                return;
            }

            if (displayStopHandled) {
                return;
            }

            displayStopHandled = true;
            // Niet de hele opname stoppen: degradeer naar alleen-microfoon en
            // laat de gebruiker de bron desgewenst opnieuw delen.
            void degradeToMicrophoneOnly(displayStream);
        };

        displayStream.addEventListener('inactive', handleDisplayEnded);
        audioTracks.forEach(track => track.addEventListener('ended', handleDisplayEnded));
        videoTracks.forEach(track => track.addEventListener('ended', handleDisplayEnded));

        return async () => {
            displayStream.removeEventListener('inactive', handleDisplayEnded);
            audioTracks.forEach(track => track.removeEventListener('ended', handleDisplayEnded));
            videoTracks.forEach(track => track.removeEventListener('ended', handleDisplayEnded));
        };
    };

    const runCleanupCallbacks = async (callbacks: Array<() => Promise<void> | void>) => {
        for (const cleanup of callbacks) {
            try {
                await cleanup();
            } catch (cleanupError) {
                logger.warn('Fout bij uitvoeren van cleanup callback:', cleanupError);
            }
        }
    };

    const stopSourceAudioWatch = React.useCallback(() => {
        if (sourceWatchCleanupRef.current) {
            sourceWatchCleanupRef.current();
            sourceWatchCleanupRef.current = null;
        }
    }, []);

    // Bewaakt gedurende de hele opname doorlopend de audio van de gedeelde
    // bron. Waarschuwt na `silenceWarnMs` aanhoudende stilte (een stille
    // meetingstart of een gewone gesprekspauze is geen fout) en trekt de
    // waarschuwing weer in zodra er geluid binnenkomt. Anders dan een eenmalige
    // check blijft de meting de hele opname doorlopen, zodat een loopback die
    // halverwege de meeting stilvalt (Bluetooth-wissel, OS-mixer-hiccup) alsnog
    // wordt opgemerkt.
    const startSourceAudioWatch = React.useCallback((stream: MediaStream, silenceWarnMs = 15000, silenceMessage?: string) => {
        stopSourceAudioWatch();

        const audioTrack = stream.getAudioTracks()[0];
        if (!audioTrack || audioTrack.readyState !== 'live') {
            return;
        }

        let audioContext: AudioContext | null = null;
        let source: MediaStreamAudioSourceNode | null = null;
        try {
            audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            if (audioContext.state === 'suspended') {
                void audioContext.resume().catch(() => undefined);
            }

            const data = new Uint8Array(analyser.frequencyBinCount);
            const threshold = 5; // zelfde drempel als detectActiveAudio (attentionNotification.ts)
            // Start de stilteteller bij nu: voor de allereerste waarschuwing geldt
            // zo dezelfde marge als voor een latere stilteperiode.
            let lastSignalAt = Date.now();
            let warned = false;

            const cleanup = () => {
                clearInterval(intervalId);
                audioTrack.removeEventListener('ended', cleanup);
                try {
                    source?.disconnect();
                } catch {
                    // Bron was al losgekoppeld.
                }
                void audioContext?.close().catch(() => undefined);
                if (sourceWatchCleanupRef.current === cleanup) {
                    sourceWatchCleanupRef.current = null;
                }
            };

            // setInterval (geen requestAnimationFrame): de meting moet doorlopen
            // terwijl de gebruiker in het meeting-tabblad zit en dit tabblad op
            // de achtergrond staat.
            const intervalId = setInterval(() => {
                if (audioTrack.readyState !== 'live') {
                    // De gedeelde bron is definitief weg; de display-ended
                    // afhandeling neemt het over (degraderen naar mic-only).
                    cleanup();
                    return;
                }

                analyser.getByteFrequencyData(data);
                const average = data.reduce((sum, value) => sum + value, 0) / data.length;

                if (average > threshold) {
                    // Geluid komt (weer) binnen: stilteteller resetten en een
                    // eerdere stiltewaarschuwing intrekken.
                    lastSignalAt = Date.now();
                    if (warned) {
                        warned = false;
                        setRecordingSourceWarning(null);
                    }
                    return;
                }

                if (!warned && Date.now() - lastSignalAt >= silenceWarnMs) {
                    warned = true;
                    setRecordingSourceWarning(prev => prev
                        ?? silenceMessage
                        ?? t('recorder.warnings.sourceSilence'));
                }
            }, 1000);

            audioTrack.addEventListener('ended', cleanup);
            sourceWatchCleanupRef.current = cleanup;
        } catch (watchError) {
            logger.warn('Kon systeemaudio-bewaking niet starten:', watchError);
            try {
                source?.disconnect();
            } catch {
                // Bron was al losgekoppeld.
            }
            void audioContext?.close().catch(() => undefined);
        }
    }, [stopSourceAudioWatch, t]);

    // Schermdeling gestopt tijdens een lopende opname (meestal via de "Stop met
    // delen"-balk van de browser). De opname niet afbreken: de mixer houdt de
    // microfoon verbonden met de destination, dus we koppelen alleen de
    // systeembron los en lopen naadloos door als alleen-microfoon. De gebruiker
    // krijgt de kans om de bron opnieuw te delen via "Wissel van bron".
    const degradeToMicrophoneOnly = React.useCallback(async (endedDisplayStream: MediaStream) => {
        const session = recorderSessionRef.current;
        if (!session) {
            return;
        }

        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state === 'inactive') {
            // Geen lopende opname meer (bijv. een normale stop die de tracks
            // beëindigt): de reguliere stop-afhandeling doet hier het werk.
            return;
        }

        stopSourceAudioWatch();

        // Systeembron uit de mix halen; de microfoon blijft doorlopen.
        if (session.mixerControl) {
            try {
                await session.mixerControl.detachSystemStream();
            } catch (detachError) {
                logger.warn('Kon systeembron niet loskoppelen uit de mix:', detachError);
            }
        }

        // Display-stream uit de sessie verwijderen en zijn listeners opruimen.
        const displayCleanup = session.displayStreamCleanup;
        if (displayCleanup) {
            await displayCleanup();
            session.cleanupCallbacks = session.cleanupCallbacks.filter(cleanup => cleanup !== displayCleanup);
            session.displayStreamCleanup = null;
        }
        session.additionalStreams = session.additionalStreams.filter(stream => stream !== endedDisplayStream);
        stopStreamTracks(endedDisplayStream);
        session.systemStreamLabel = null;

        // Live-modus terug naar alleen-microfoon, zodat de UI "Scherm toevoegen"
        // toont en handleSwitchScreen de bron weer kan inmixen.
        session.activeLiveMode = 'microphone';
        setActiveLiveMode('microphone');

        setUserStatusMessage('');
        setRecordingSourceWarning(t('recorder.warnings.screenSharingStopped'));
    }, [stopSourceAudioWatch, t]);

    useEffect(() => () => {
        stopSourceAudioWatch();
    }, [stopSourceAudioWatch]);

    // #1: detecteer een draadloze/Bluetooth-audio-uitvoer zodat de wizard kan
    // adviseren om bij oortjes de tab-audio-route te gebruiken. Herhaalt bij
    // elke apparaatwijziging (oortjes koppelen/ontkoppelen).
    useEffect(() => {
        if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
            return;
        }
        let cancelled = false;
        const refresh = () => {
            void detectWirelessAudioOutput().then(detected => {
                if (!cancelled) setWirelessOutputDetected(detected);
            });
        };
        refresh();
        navigator.mediaDevices.addEventListener?.('devicechange', refresh);
        return () => {
            cancelled = true;
            navigator.mediaDevices.removeEventListener?.('devicechange', refresh);
        };
    }, []);

    // #2: herstel bij een apparaatwijziging tijdens een lopende systeemaudio-
    // opname. Het standaard render-device wisselen (bijv. Bluetooth-oortjes
    // verbinden) kan de loopback-track stil laten vallen zonder dat
    // track.muted/readyState dat tonen (Chromium #1432877). We herbewaken de
    // bron met een korte drempel en een gerichte boodschap; blijft het geluid
    // gewoon doorkomen, dan verdwijnt de waarschuwing vanzelf.
    useEffect(() => {
        const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
        if (!mediaDevices?.addEventListener) {
            return;
        }
        const handleDeviceChange = () => {
            const recorder = mediaRecorderRef.current;
            if (!recorder || recorder.state === 'inactive') {
                return;
            }
            if (isSwitchingDisplaySourceRef.current) {
                return;
            }
            // Inline lookup van de actieve display-stream (de bron met een
            // videotrack) i.p.v. getCurrentDisplayStream, zodat dit effect geen
            // instabiele functie-dependency krijgt.
            const displayStream = recorderSessionRef.current?.additionalStreams.find(stream => stream.getVideoTracks().length > 0) ?? null;
            if (!displayStream) {
                // Alleen-microfoon: er is geen loopback om te verliezen.
                return;
            }
            // Het wisselen van audio-apparaat kan de systeem-loopback stil laten
            // vallen (Chromium #1432877). Doe een korte gerichte probe; de
            // doorlopende bronbewaking (startSourceAudioWatch) blijft ondertussen
            // ook actief en trekt de waarschuwing weer in zodra er geluid is.
            void probeStreamHasSignal(displayStream, 2000).then(hasSignal => {
                if (!hasSignal) {
                    setRecordingSourceWarning(prev => prev ?? DEVICE_CHANGE_SYSTEM_AUDIO_MESSAGE);
                }
            });
        };
        mediaDevices.addEventListener('devicechange', handleDeviceChange);
        return () => mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    }, []);

    // #3: tijdens de verificatiestap bewaken of er werkelijk signaal binnenkomt
    // op de gedeelde bron. Komt er na enkele seconden niets, dan tonen we de
    // (soft) stiltewaarschuwing met het advies om bij oortjes naar tab-audio te
    // wisselen. Zodra er signaal is, trekken we juist die waarschuwing weer in.
    useEffect(() => {
        if (wizardStep !== 'verification' || !previewStreams?.system || verificationError) {
            return;
        }
        const startedAt = Date.now();
        let sawSignal = false;
        const intervalId = setInterval(() => {
            if (systemPreviewLevelRef.current > SYSTEM_SIGNAL_LEVEL_THRESHOLD) {
                sawSignal = true;
                setVerificationWarning(prev => (prev === SYSTEM_SILENCE_VERIFICATION_MESSAGE ? null : prev));
                return;
            }
            if (!sawSignal && Date.now() - startedAt >= 6000) {
                setVerificationWarning(prev => prev ?? SYSTEM_SILENCE_VERIFICATION_MESSAGE);
            }
        }, 500);
        return () => clearInterval(intervalId);
    }, [wizardStep, previewStreams?.system, verificationError]);

    // Stop preview-streams (mic + schermdeling) bij unmount, tenzij ze al zijn
    // overgedragen aan een lopende opname (dan staan ze in additionalStreams en
    // bezit de sessie ze). Voorkomt dat een actieve schermdeling blijft hangen
    // wanneer de component verdwijnt terwijl de wizard nog openstond.
    useEffect(() => () => {
        const pending = previewStreamsRef.current;
        if (!pending) return;
        const owned = recorderSessionRef.current?.additionalStreams ?? [];
        [pending.mic, pending.system].forEach(stream => {
            if (stream && !owned.includes(stream)) {
                stream.getTracks().forEach(track => {
                    try {
                        track.stop();
                    } catch {
                        // Track was al gestopt.
                    }
                });
            }
        });
    }, []);

    const cleanupSessionMediaResources = React.useCallback(async (sessionArg?: RecorderSessionStore | null) => {
        const session = sessionArg ?? recorderSessionRef.current;
        if (!session) {
            return;
        }

        stopSourceAudioWatch();
        stopStreamTracks(session.mediaStream);
        session.mediaStream = null;

        session.additionalStreams.forEach(additionalStream => stopStreamTracks(additionalStream));
        session.additionalStreams = [];

        if (session.cleanupCallbacks.length > 0) {
            const callbacks = [...session.cleanupCallbacks];
            session.cleanupCallbacks = [];
            await runCleanupCallbacks(callbacks);
        }
        session.displayStreamCleanup = null;

        if (session.audioContext && session.audioContext.state !== 'closed') {
            try {
                await session.audioContext.close();
            } catch (audioContextError) {
                logger.warn('Kon AudioContext niet sluiten:', audioContextError);
            }
        }
        session.audioContext = null;
        session.activeLiveMode = session.preferredLiveMode ?? 'microphone';
        isSwitchingDisplaySourceRef.current = false;
    }, [stopSourceAudioWatch]);

    const acquireRecordingResources = React.useCallback(async (mode: LiveRecordingType): Promise<RecordingSetupResult> => {
        if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
            throw new Error(t('recorder.errors.mediaDevicesUnavailable'));
        }

        const cleanupCallbacks: Array<() => Promise<void> | void> = [];



        if (!navigator.mediaDevices.getDisplayMedia) {
            const fallbackStream = await navigator.mediaDevices.getUserMedia({
                audio: MICROPHONE_AUDIO_CONSTRAINTS
            });

            return {
                recorderStream: fallbackStream,
                additionalStreams: [],
                audioContext: null,
                cleanupCallbacks: [],
                displayStream: null,
                warningMessage: systemAudioSupport.reason || t('recorder.errors.systemAudioUnsupportedFallback')
            };
        }

        let microphoneStream: MediaStream | null = null;
        try {
            microphoneStream = await navigator.mediaDevices.getUserMedia({
                audio: MICROPHONE_AUDIO_CONSTRAINTS
            });
        } catch (microphoneError) {
            throw microphoneError;
        }

        // Always use mixer for microphone mode to allow adding screen later
        if (mode === 'microphone') {
            try {
                const mixed = await createMixedAudioStream(null, microphoneStream);
                cleanupCallbacks.push(mixed.cleanup);

                return {
                    recorderStream: mixed.stream,
                    additionalStreams: [microphoneStream],
                    audioContext: mixed.audioContext,
                    cleanupCallbacks,
                    displayStream: null,
                    mixerControl: mixed
                };
            } catch (mixError) {
                stopStreamTracks(microphoneStream);
                throw mixError;
            }
        }

        let displayStream: MediaStream | null = null;
        try {
            displayStream = await navigator.mediaDevices.getDisplayMedia(buildDisplayMediaConstraints(!!systemAudioSupport.screenAudio));
        } catch (displayError) {
            // If display media fails, we might want to stop the mic stream we just got?
            // Or fallback to mic only? The original logic fell back to mic only.
            // But if we already have mic stream, we can just use it.

            // However, the original logic for fallback was:
            // const fallbackStream = await navigator.mediaDevices.getUserMedia(...)

            // Since we already have microphoneStream, we can use it as fallback.

            let warningMessage = t('recorder.errors.systemAudioNotShared');
            if (displayError instanceof DOMException && displayError.name === 'NotAllowedError') {
                warningMessage = t('recorder.errors.screenSharingDenied');
            }

            return {
                recorderStream: microphoneStream,
                additionalStreams: [],
                audioContext: null,
                cleanupCallbacks: [],
                displayStream: null,
                warningMessage
            };
        }

        displayStream.getVideoTracks().forEach(track => {
            track.enabled = false;
        });

        const displayHasAudio = displayStream.getAudioTracks().length > 0;
        // Get label from the video track before disabling/stopping it, or from the stream itself if possible.
        // Usually the video track label contains the window/tab title.
        const systemLabel = formatSystemStreamLabel(displayStream.getVideoTracks()[0]?.label);

        if (!displayHasAudio || !microphoneStream) {
            const missingAudioMessage = buildMissingSystemAudioMessage(displayStream);
            stopStreamTracks(displayStream);
            displayStream = null;

            if (!microphoneStream) {
                throw new Error(t('recorder.errors.microphoneCouldNotStart'));
            }

            // If we are here, it means we wanted system audio but didn't get it.
            // Fallback to microphone only via mixer so we can add screen later
            try {
                const mixed = await createMixedAudioStream(null, microphoneStream);
                cleanupCallbacks.push(mixed.cleanup);

                return {
                    recorderStream: mixed.stream,
                    additionalStreams: [microphoneStream],
                    audioContext: mixed.audioContext,
                    cleanupCallbacks,
                    displayStream: null,
                    mixerControl: mixed,
                    warningMessage: missingAudioMessage
                };
            } catch (mixError) {
                stopStreamTracks(microphoneStream);
                throw mixError;
            }
        }

        try {
            const mixed = await createMixedAudioStream(displayStream, microphoneStream);
            cleanupCallbacks.push(mixed.cleanup);

            return {
                recorderStream: mixed.stream,
                additionalStreams: [microphoneStream, displayStream],
                audioContext: mixed.audioContext,
                cleanupCallbacks,
                displayStream,
                warningMessage: undefined,
                mixerControl: mixed,
                systemStreamLabel: systemLabel
            };
        } catch (mixError) {
            stopStreamTracks(displayStream);
            stopStreamTracks(microphoneStream);

            if (cleanupCallbacks.length > 0) {
                await runCleanupCallbacks(cleanupCallbacks);
            }
            throw mixError;
        }
    }, [systemAudioSupport.reason, systemAudioSupport.screenAudio, t]);

    // Koppelt de meetingnotities na een geslaagde upload als ondersteunend
    // .txt-document aan de opname. Mislukt dit, dan blijft de opname zelf intact;
    // we proberen het een paar keer en loggen anders stil (geen blokkerende fout).
    const attachMeetingNotesToSupportingDocuments = React.useCallback(async (fileId: string) => {
        const notes = normalizeMeetingNotes(meetingNotesDraftRef.current);
        if (!notes || !fileId) return;

        const timestamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
        const filename = `${t('recorder.notes.supportingDocFilenamePrefix')}-${timestamp}.txt`;
        const retryableStatuses = new Set([404, 409, 425, 429, 500, 502, 503, 504]);

        for (let attempt = 0; attempt < 3; attempt += 1) {
            if (attempt > 0) {
                await new Promise((resolve) => setTimeout(resolve, attempt * 1200));
            }
            try {
                await apiService.addSupportingDocumentContent(fileId, notes, filename);
                break;
            } catch (error) {
                const status = (error as { response?: { status?: number } })?.response?.status;
                if (status && retryableStatuses.has(status) && attempt < 2) {
                    continue;
                }
                if (attempt === 2) {
                    logger.warn('Kon notities niet koppelen aan ondersteunende documenten:', error);
                }
            }
        }

        // De opname is geüpload; wis de notities (en daarmee de gedeelde sessie)
        // zodat ze niet bij een volgende opname blijven hangen. Mislukte koppeling
        // is hierboven al gelogd.
        setMeetingNotesDraft('');
    }, [apiService, t]);

    const uploadMicrophoneRecording = React.useCallback(async (blob: Blob, startedAt: string | null, preferredFileId?: string) => {
        setErrorMessage('');
        setUserStatusMessage('');
        const partnerTarget = takePartnerRecordingTarget();
        const fileId = await uploadService.uploadMicrophoneRecording({
            blob,
            recordingStartTime: startedAt,
            preferredFileId,
            onComplete: (id) => {
                if (onRecordingComplete) {
                    onRecordingComplete(id);
                }
            },
            onServerFileId: partnerTarget
                ? (serverFileId) => { void applyPartnerRecordingTarget(serverFileId, partnerTarget); }
                : undefined
        });

        if (fileId) {
            await attachMeetingNotesToSupportingDocuments(fileId);
        } else {
            const latestState = uploadService.getState();
            if (latestState.error) {
                setErrorMessage(latestState.error);
            }
        }
    }, [attachMeetingNotesToSupportingDocuments, onRecordingComplete, uploadService]);

    const processRecordedAudio = React.useCallback(async () => {
        let blob: Blob | null = null;
        const completedRecordingId = currentRecordingIdRef.current;
        const completedChunks = [...recordedChunksRef.current];
        const expectedByteSize = calculateRecorderChunksSize(completedChunks);
        const memoryBlob = expectedByteSize > 0
            ? createRecorderBlob(completedChunks, recorderMimeTypeRef.current)
            : null;

        if (completedRecordingId) {
            try {
                await waitForPendingChunkWrites();
            } catch (pendingChunkError) {
                logger.warn('Error while waiting for local chunk writes:', pendingChunkError);
            }
        }

        if (isBlobCompleteForChunks(memoryBlob, completedChunks)) {
            blob = memoryBlob;
        }

        // IndexedDB stays as fallback for recovery if in-memory assembly is unavailable.
        if ((!blob || blob.size === 0) && completedRecordingId) {
            try {
                const assembledBlob = await assembleRecordingFromChunks(completedRecordingId, recorderMimeTypeRef.current);
                if (assembledBlob && assembledBlob.size > 0) {
                    if (expectedByteSize === 0 || assembledBlob.size === expectedByteSize) {
                        blob = assembledBlob;
                    } else {
                        logger.warn('IndexedDB assembled blob size mismatch, skipping fallback blob.', {
                            recordingId: completedRecordingId,
                            expectedByteSize,
                            actualByteSize: assembledBlob.size
                        });
                    }
                }
            } catch (error) {
                logger.error('Error assembling recording from DB:', error);
            }
        }

        if (lastChunkPersistenceErrorRef.current) {
            logger.warn('Some recorder chunks could not be persisted locally; continuing with the in-memory upload blob.', lastChunkPersistenceErrorRef.current);
        }

        recordedChunksRef.current = [];
        recorderSessionRef.current.recordedChunks = recordedChunksRef.current;
        pendingChunkWritePromisesRef.current.clear();
        lastChunkPersistenceErrorRef.current = null;

        // Clear recording ID
        currentRecordingIdRef.current = null;
        recorderSessionRef.current.recordingId = null;

        if (!blob || blob.size === 0) {
            setErrorMessage(t('recorder.errors.noAudioData'));
            return;
        }

        const startedAt = recordingStartTime;
        updateRecordingStartTime(null);
        setRecordingTime(0);

        await uploadMicrophoneRecording(blob, startedAt, completedRecordingId ?? undefined);
    }, [recordingStartTime, t, updateRecordingStartTime, uploadMicrophoneRecording, waitForPendingChunkWrites]);

    const processFileUpload = React.useCallback(async (file: File) => {
        setErrorMessage('');
        setUserStatusMessage('');
        const partnerTarget = takePartnerRecordingTarget();
        const fileId = await uploadService.uploadFile({
            file,
            onComplete: (id) => {
                if (onRecordingComplete) {
                    onRecordingComplete(id);
                }
            },
            onServerFileId: partnerTarget
                ? (serverFileId) => { void applyPartnerRecordingTarget(serverFileId, partnerTarget); }
                : undefined
        });

        if (fileId) {
            await attachMeetingNotesToSupportingDocuments(fileId);
        } else {
            const latestState = uploadService.getState();
            if (latestState.error) {
                setErrorMessage(latestState.error);
            }
        }
    }, [attachMeetingNotesToSupportingDocuments, onRecordingComplete, uploadService]);

    const resumePendingMicrophoneUpload = React.useCallback(async () => {
        if (!pendingMicrophoneRecovery) {
            return;
        }

        if (!user) {
            setUserStatusMessage(t('recorder.errors.loginToResumeRecording'));
            return;
        }

        setUserStatusMessage('');
        const success = await uploadService.resumePendingMicrophoneUpload({
            onComplete: (id) => {
                if (onRecordingComplete) {
                    onRecordingComplete(id);
                }
            }
        });

        if (!success) {
            const latestState = uploadService.getState();
            if (latestState.error) {
                setErrorMessage(latestState.error);
            }
        }
    }, [onRecordingComplete, pendingMicrophoneRecovery, t, uploadService, user]);

    const discardPendingMicrophoneRecording = React.useCallback(async () => {
        setUserStatusMessage('');
        await uploadService.discardPendingMicrophoneRecording();
    }, [uploadService]);

    const resumePendingFileUpload = React.useCallback(async () => {
        if (!pendingFileRecovery) {
            return;
        }

        if (!user) {
            setUserStatusMessage(t('recorder.errors.loginToResumeFile'));
            return;
        }

        setUserStatusMessage('');
        const success = await uploadService.resumePendingFileUpload({
            onComplete: (id) => {
                if (onRecordingComplete) {
                    onRecordingComplete(id);
                }
            }
        });

        if (!success) {
            const latestState = uploadService.getState();
            if (latestState.error) {
                setErrorMessage(latestState.error);
            }
        }
    }, [onRecordingComplete, pendingFileRecovery, t, uploadService, user]);

    const discardPendingFileUpload = React.useCallback(async () => {
        setUserStatusMessage('');
        await uploadService.discardPendingFileUpload();
    }, [uploadService]);

    // Foutmelding wegklikken: zowel de lokale als de uploadService-fout opruimen
    // zodat de melding niet meteen weer terugkomt.
    const dismissError = React.useCallback(() => {
        setErrorMessage('');
        uploadService.clearError();
    }, [uploadService]);

    // Statusmelding wegklikken. Een herstelbare opname wordt daarbij alleen
    // verborgen, niet gewist: dit is vaak de enige kopie van de opname. Bij de
    // volgende keer openen verschijnt de melding weer; echt weggooien kan alleen
    // via "Verwijderen".
    const dismissStatusMessage = React.useCallback(() => {
        setUserStatusMessage('');
        const recovery = pendingMicrophoneRecovery ?? pendingFileRecovery;
        if (recovery) {
            setDismissedRecoveryId(recovery.id);
        }
    }, [pendingFileRecovery, pendingMicrophoneRecovery]);

    // Verwijderen van een herstelbare opname wist de lokale kopie definitief, dus
    // net als bij het weggooien van een lopende opname eerst een tweede klik vragen.
    const requestDiscardPendingRecovery = () => {
        if (confirmRecoveryDeleteTimeoutRef.current) {
            clearTimeout(confirmRecoveryDeleteTimeoutRef.current);
            confirmRecoveryDeleteTimeoutRef.current = null;
        }

        if (isConfirmingRecoveryDelete) {
            setIsConfirmingRecoveryDelete(false);
            void (pendingMicrophoneRecovery ? discardPendingMicrophoneRecording() : discardPendingFileUpload());
            return;
        }

        setIsConfirmingRecoveryDelete(true);
        confirmRecoveryDeleteTimeoutRef.current = setTimeout(() => {
            confirmRecoveryDeleteTimeoutRef.current = null;
            setIsConfirmingRecoveryDelete(false);
        }, 4000);
    };

    const isActivelyRecording = isStartingRecording || isRecording || isPaused
        || (isUploading && !isUploadComplete) || isProcessingRecording;

    // Zichtbaarheid van de recorderkaart bepalen met een IntersectionObserver
    // (threshold 0.15 zoals desktop); zo weet de zwevende indicator wanneer hij
    // moet verschijnen.
    useEffect(() => {
        const element = recorderRootRef.current;
        if (!canShowContainer || !element) {
            setIsRecorderInView(false);
            return undefined;
        }

        if (typeof IntersectionObserver === 'undefined') {
            setIsRecorderInView(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            ([entry]) => setIsRecorderInView(entry.isIntersecting),
            { threshold: 0.15 }
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, [canShowContainer]);

    const statusLabel = (() => {
        if (isStartingRecording) return t('recorder.statusLabel.starting');
        if (isRecording) return t('recorder.statusLabel.recording');
        if (isPaused) return t('recorder.statusLabel.paused');
        if (isProcessingRecording) return t('recorder.statusLabel.processing');
        if (isUploading && !isUploadComplete) return t('recorder.statusLabel.uploading');
        if (visibleErrorMessage) return t('recorder.statusLabel.actionRequired');
        if (isUploadComplete) return t('recorder.statusLabel.uploadComplete');
        return t('recorder.statusLabel.ready');
    })();

    const floatingStatusLabel = (() => {
        if (isStartingRecording) return t('recorder.floatingStatusLabel.starting');
        if (isPaused) return t('recorder.floatingStatusLabel.paused');
        if (isProcessingRecording || isUploading) return t('recorder.floatingStatusLabel.uploading');
        if (isRecording) return t('recorder.floatingStatusLabel.recording');
        return t('recorder.floatingStatusLabel.idle');
    })();

    const clearDiscardConfirm = React.useCallback(() => {
        if (confirmDiscardTimeoutRef.current) {
            clearTimeout(confirmDiscardTimeoutRef.current);
            confirmDiscardTimeoutRef.current = null;
        }
        setIsConfirmingDiscard(false);
    }, []);

    const requestDiscardRecording = () => {
        if (isConfirmingDiscard) {
            clearDiscardConfirm();
            void handleDiscardRecording();
            return;
        }

        setIsConfirmingDiscard(true);
        if (confirmDiscardTimeoutRef.current) {
            clearTimeout(confirmDiscardTimeoutRef.current);
        }
        confirmDiscardTimeoutRef.current = setTimeout(() => {
            confirmDiscardTimeoutRef.current = null;
            setIsConfirmingDiscard(false);
        }, 4000);
    };

    useEffect(() => () => {
        if (confirmDiscardTimeoutRef.current) {
            clearTimeout(confirmDiscardTimeoutRef.current);
        }
        if (confirmRecoveryDeleteTimeoutRef.current) {
            clearTimeout(confirmRecoveryDeleteTimeoutRef.current);
        }
    }, []);

    // Bevestigingen vervallen zodra de opname-staat wisselt (bijv. hervat).
    useEffect(() => {
        clearDiscardConfirm();
    }, [isPaused, isRecording, clearDiscardConfirm]);

    const handleFloatingIndicatorClick = () => {
        if (location.pathname !== '/recordings') {
            navigate('/recordings');
            return;
        }

        recorderRootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // Zwevende indicator: actief aan het opnemen, maar de recorderkaart is niet
    // (voldoende) in beeld of de gebruiker zit op een andere route.
    const shouldShowFloating = isActivelyRecording && (!canShowContainer || !isRecorderInView);

    const renderFloatingIndicator = () => {
        if (!shouldShowFloating || typeof document === 'undefined') {
            return null;
        }

        return createPortal(
            <button
                type="button"
                className={`recorder-floating-indicator ${isPaused ? 'is-paused' : ''}`}
                onClick={handleFloatingIndicatorClick}
                aria-label={t('recorder.floatingIndicator.ariaLabel')}
            >
                <span className="recorder-floating-dot" aria-hidden="true" />
                <span className="recorder-floating-time">{formatTime(recordingTime)}</span>
                <span className="recorder-floating-label">{floatingStatusLabel}</span>
            </button>,
            document.body
        );
    };


    const startRecordingWithMode = async (mode: LiveRecordingType) => {
        if (isStartingRecording) {
            return;
        }

        if (!user) {
            setErrorMessage(t('recorder.errors.loginRequiredToRecord'));
            return;
        }

        setPreferredLiveMode(mode);
        setIsStartingRecording(true);
        setIsLiveModeDialogOpen(false);
        setErrorMessage('');
        setUserStatusMessage('');
        setExtensionStatusMessage('');

        try {
            const resultingMode = await startLiveRecording(mode);
            setActiveLiveMode(resultingMode);
        } catch (error) {
            logger.error('Error starting recording:', error);
            let displayMessage = t('recorder.errors.startRecordingFailed');
            let statusMessage = '';

            if (error instanceof DOMException) {
                switch (error.name) {
                    case 'NotAllowedError':
                    case 'SecurityError':
                        displayMessage = mode === 'microphone+system'
                            ? t('recorder.errors.accessDeniedScreenMic')
                            : t('recorder.errors.accessDeniedMic');
                        break;
                    case 'NotFoundError':
                        displayMessage = t('recorder.errors.noMicrophoneFound');
                        break;
                    case 'NotReadableError':
                        displayMessage = t('recorder.errors.deviceInUse');
                        break;
                    case 'NotSupportedError':
                        displayMessage = t('recorder.errors.recordingFormatUnsupported');
                        break;
                    case 'AbortError':
                        displayMessage = '';
                        statusMessage = mode === 'microphone+system'
                            ? t('recorder.status.screenSharingCancelled')
                            : t('recorder.status.recordingCancelled');
                        break;
                    default:
                        break;
                }
            } else if (error instanceof Error && error.message) {
                displayMessage = error.message;
            }

            setErrorMessage(displayMessage);
            if (statusMessage) {
                setUserStatusMessage(statusMessage);
            }
        } finally {
            setIsStartingRecording(false);
        }
    };

    // Vraagt mic + schermdeling aan en start de opname zodra er systeemaudio
    // meekomt; zonder audio schakelt de wizard naar de verificatiestap. Wordt
    // zowel door de instructiestap als door "Opnieuw proberen" gebruikt, zodat
    // een vergeten audio-vinkje maar één klik kost om te herstellen.
    const acquireStreamsAndStartRecording = async () => {
        setIsStartingRecording(true);
        // Wis een eventuele fout/status van een vorige poging, anders blijft die
        // bovenaan de dialog staan terwijl de kiezer alweer open is.
        setErrorMessage('');
        setUserStatusMessage('');
        // Buiten de try zodat de catch de mic-stream kan opruimen wanneer een
        // latere stap (schermkiezer) faalt of wordt geannuleerd.
        let micStream: MediaStream | null = null;
        try {
            // Request notification permission upfront (for later use)
            await requestNotificationPermission();

            // 1. Get Mic Stream FIRST
            micStream = await navigator.mediaDevices.getUserMedia({
                audio: MICROPHONE_AUDIO_CONSTRAINTS
            });

            // 2. Get Display Stream SECOND
            const displayStream = await navigator.mediaDevices.getDisplayMedia(buildDisplayMediaConstraints(!!systemAudioSupport.screenAudio));

            // Check if audio track exists
            const audioTracks = displayStream.getAudioTracks();
            if (audioTracks.length === 0) {
                const missingAudioMessage = buildMissingSystemAudioMessage(displayStream);
                stopStreamTracks(displayStream);
                setVerificationError(missingAudioMessage);

                const streams = {
                    system: displayStream,
                    mic: micStream
                };
                setPreviewStreams(streams);
                setWizardStep('verification');

                // Trigger attention to bring user back
                triggerAttention(t('recorder.errors.noSystemAudioAttention'));
                return;
            }

            // #3: er is wel een audiotrack, maar bij draadloze oortjes levert de
            // loopback soms een live-maar-stille track op (Chromium #1432877).
            // Meet daarom kort of er werkelijk signaal binnenkomt vóórdat we
            // automatisch starten. Geen signaal -> de gebruiker eerst laten
            // controleren in de verificatiestap (met live meter en het advies om
            // bij oortjes naar tab-audio te wisselen). Wel signaal -> direct door.
            const hasSignal = await probeStreamHasSignal(displayStream);
            if (!hasSignal) {
                displayStream.getVideoTracks().forEach(track => {
                    track.enabled = false;
                });
                setVerificationError(null);
                // Niet meteen waarschuwen: de korte probe (1,5s) ziet een aanloop-
                // stilte snel aan voor "geen geluid", terwijl de live meter er vaak
                // direct daarna wél signaal in ziet. Dat gaf een flits van "Geen
                // geluid gedetecteerd". Laat de stiltebewaking in de verificatiestap
                // (6s-drempel, live meter) als enige beslissen of er echt stilte is.
                setVerificationWarning(null);
                setPreviewStreams({
                    system: displayStream,
                    mic: micStream
                });
                setWizardStep('verification');

                // Trigger attention to bring user back
                triggerAttention(t('recorder.errors.checkSystemAudioAttention'));
                return;
            }

            // Audio tracks exist and signal detected - AUTO START recording immediately
            logger.log('[Notizy Debug] Audio signal detected, auto-starting recording...');

            // Check for source selection warnings (window/screen instead of tab)
            const sourceWarning = buildDisplaySelectionWarning(displayStream, !!systemAudioSupport.screenAudio);
            setRecordingSourceWarning(sourceWarning);

            setVerificationError(null);
            setVerificationWarning(null);
            setIsLiveModeDialogOpen(false);
            setWizardStep('mode-selection');

            const streams = {
                system: displayStream,
                mic: micStream
            };

            try {
                await startLiveRecording('microphone+system', streams);
                // Ownership transferred, don't set previewStreams.
                // De doorlopende audiobewaking (startSourceAudioWatch, gestart in
                // startLiveRecording) waarschuwt bij aanhoudende stilte en ruimt
                // de waarschuwing zelf op zodra er geluid binnenkomt.
            } catch (err) {
                logger.error("Failed to auto-start from wizard", err);
                setErrorMessage(t('recorder.errors.autoStartFailed'));
                // Cleanup on failure
                stopStreamTracks(displayStream);
                stopStreamTracks(micStream);
            }
        } catch (err) {
            logger.error('Error getting streams:', err);
            // Annuleert de gebruiker de schermkiezer (of faalt het verkrijgen van
            // een stream), dan is de eerder verkregen microfoon-stream nooit
            // overgedragen aan een opname of preview. Stop hem expliciet, anders
            // blijft het microfoon-indicatorlampje branden tot een page reload.
            stopStreamTracks(micStream);

            // Annuleert de gebruiker de kiezer zelf, dan is er niets aan de hand:
            // toon hooguit een neutrale statusmelding. Bij een échte fout (bijv.
            // een constraint die de browser niet kan leveren, of beleid dat
            // schermdeling blokkeert) zag de gebruiker tot nu toe NIETS — alleen
            // de console. Maak die fout zichtbaar, inclusief de DOMException-naam,
            // zodat browser-specifieke problemen (zoals op Edge) te herkennen zijn.
            if (err instanceof DOMException && (err.name === 'AbortError' || err.name === 'NotAllowedError')) {
                setUserStatusMessage(t('recorder.status.screenSharingCancelled'));
            } else {
                const detail = err instanceof DOMException
                    ? `${err.name}: ${err.message}`
                    : err instanceof Error && err.message
                        ? err.message
                        : t('recorder.errors.unknownError');
                setErrorMessage(t('recorder.errors.screenSharingFailedDetail', { detail }));
            }
        } finally {
            setIsStartingRecording(false);
        }
    };

    const handlePauseRecording = async () => {
        try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.pause();
                setIsPaused(true);
                setIsRecording(false);
                stopTimer();
            }
        } catch (error) {
            logger.error('Error pausing recording:', error);
            setErrorMessage(t('recorder.errors.pauseFailed'));
        }
    };

    const handleResumeRecording = async () => {
        try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
                mediaRecorderRef.current.resume();
                setIsPaused(false);
                setIsRecording(true);
                startTimer(recordingTime);
            }
        } catch (error) {
            logger.error('Error resuming recording:', error);
            setErrorMessage(t('recorder.errors.resumeFailed'));
        }
    };

    const handleDiscardRecording = async () => {
        try {
            // Stop en gooi opname weg. De vlag blijft staan tot de volgende
            // opname start, zodat een asynchroon binnenkomend stop-event de
            // weggegooide opname nooit alsnog verwerkt en uploadt.
            isDiscardingRef.current = true;

            const recorder = mediaRecorderRef.current;
            if (recorder) {
                if (recorder.state !== 'inactive') {
                    // Handlers definitief loskoppelen vóór stop(); het stop- en
                    // laatste dataavailable-event vuren asynchroon, dus de
                    // handlers mogen hierna niet worden teruggezet.
                    recorder.ondataavailable = null;
                    recorder.onstop = null;
                    recorder.stop();
                }
            }

            await cleanupSessionMediaResources();

            // Reset alle state
            recordedChunksRef.current = [];
            if (recorderSessionRef.current) {
                recorderSessionRef.current.recordedChunks = recordedChunksRef.current;
                recorderSessionRef.current.mediaRecorder = null;
                recorderSessionRef.current.recordingStartTime = null;
                recorderSessionRef.current.recordingId = null;
                const nextActiveMode = recorderSessionRef.current.preferredLiveMode ?? 'microphone';
                setActiveLiveMode(nextActiveMode);
            }

            // Clean up DB if we had an ID
            if (currentRecordingIdRef.current) {
                await removeMicrophoneRecording(currentRecordingIdRef.current);
                currentRecordingIdRef.current = null;
            }

            mediaRecorderRef.current = null;
            setMediaRecorderInstance(null);
            setIsRecording(false);
            setIsPaused(false);
            setRecordingSourceWarning(null);
            setRecordingTime(0);
            updateRecordingStartTime(null);
            stopTimer();
            setErrorMessage('');
            setUserStatusMessage('');
            setExtensionStatusMessage('');
            setMeetingNotesDraft('');
        } catch (error) {
            logger.error('Error discarding recording:', error);
            setErrorMessage(t('recorder.errors.discardFailed'));
        }
    };

    const handleFinishRecording = async () => {
        try {
            // Stop en upload de opname
            await stopLiveRecording();
            setIsPaused(false);
            setRecordingSourceWarning(null);
        } catch (error) {
            logger.error('Error finishing recording:', error);
            setErrorMessage(t('recorder.errors.finishFailed'));
        }
    };

    const handleSwitchScreen = async () => {
        const session = recorderSessionRef.current;
        if (!session || !session.mixerControl) {
            logger.warn('Cannot switch screen: no mixer control available');
            return;
        }

        try {
            isSwitchingDisplaySourceRef.current = true;
            const newDisplayStream = await navigator.mediaDevices.getDisplayMedia(buildDisplayMediaConstraints(!!systemAudioSupport.screenAudio));

            const audioTracks = newDisplayStream.getAudioTracks();
            if (audioTracks.length === 0) {
                // User didn't share audio. Surface this via the existing amber
                // bron-waarschuwing (met "Wissel van bron"-CTA) i.p.v. een losse
                // melding, zodat er nooit twee soortgelijke meldingen verschijnen.
                stopStreamTracks(newDisplayStream);
                setRecordingSourceWarning(t('recorder.warnings.switchScreenNoAudio'));
                return;
            }

            // Get new label
            const newLabel = formatSystemStreamLabel(newDisplayStream.getVideoTracks()[0]?.label || t('recorder.systemAudioDefaultLabel'));

            // Disable video tracks on new stream (we only want audio)
            newDisplayStream.getVideoTracks().forEach(track => {
                track.enabled = false;
            });

            const oldDisplayStream = getCurrentDisplayStream(session);
            const oldDisplayCleanup = session.displayStreamCleanup;

            // Replace in mixer
            await session.mixerControl.replaceSystemStream(newDisplayStream);

            if (oldDisplayCleanup) {
                await oldDisplayCleanup();
                session.cleanupCallbacks = session.cleanupCallbacks.filter(cleanup => cleanup !== oldDisplayCleanup);
                session.displayStreamCleanup = null;
            }

            session.additionalStreams = session.additionalStreams.filter(stream => stream !== oldDisplayStream);
            session.additionalStreams.push(newDisplayStream);
            session.systemStreamLabel = newLabel;

            const newDisplayCleanup = registerDisplayStreamCleanup(newDisplayStream);
            session.displayStreamCleanup = newDisplayCleanup;
            session.cleanupCallbacks.push(newDisplayCleanup);

            // Cleanup old display stream after the new listeners are in place
            if (oldDisplayStream) {
                stopStreamTracks(oldDisplayStream);
            }

            // Update active mode if we were in mic only mode
            if (activeLiveMode === 'microphone') {
                setActiveLiveMode('microphone+system');
                session.activeLiveMode = 'microphone+system';
            }

            // Force re-render to show new label
            setRecordingTime(prev => prev);
            setRecordingSourceWarning(null);
            startSourceAudioWatch(newDisplayStream);
            setUserStatusMessage(t('recorder.status.sourceUpdated'));

        } catch (error) {
            if (error instanceof DOMException && error.name === 'NotAllowedError') {
                // User cancelled picker, just ignore and continue recording
                return;
            }
            logger.error('Error switching screen:', error);
            setUserStatusMessage(t('recorder.errors.switchScreenFailed'));
        } finally {
            isSwitchingDisplaySourceRef.current = false;
        }
    };

    const startLiveRecording = async (mode: LiveRecordingType, preAcquiredStreams?: { mic?: MediaStream; system?: MediaStream } | null): Promise<LiveRecordingType> => {
        const session = recorderSessionRef.current;

        let resources: RecordingSetupResult;
        if (preAcquiredStreams && mode === 'microphone+system' && preAcquiredStreams.system && preAcquiredStreams.mic) {
            // Use pre-acquired streams from Wizard
            try {
                const mixed = await createMixedAudioStream(preAcquiredStreams.system, preAcquiredStreams.mic);
                resources = {
                    recorderStream: mixed.stream,
                    additionalStreams: [preAcquiredStreams.mic, preAcquiredStreams.system],
                    audioContext: mixed.audioContext,
                    cleanupCallbacks: [mixed.cleanup],
                    displayStream: preAcquiredStreams.system,
                    warningMessage: undefined,
                    mixerControl: mixed,
                    systemStreamLabel: formatSystemStreamLabel(preAcquiredStreams.system.getVideoTracks()[0]?.label || t('recorder.systemAudioDefaultLabel'))
                };
            } catch (mixError) {
                // Fallback cleanup if mixing fails
                stopStreamTracks(preAcquiredStreams.system);
                stopStreamTracks(preAcquiredStreams.mic);
                throw mixError;
            }
        } else {
            // Standard flow
            resources = await acquireRecordingResources(mode);
        }

        const cleanupCallbacks = [...resources.cleanupCallbacks];
        let resultingMode: LiveRecordingType = 'microphone';

        isDiscardingRef.current = false;
        pendingChunkWritePromisesRef.current.clear();
        lastChunkPersistenceErrorRef.current = null;
        recordedChunksRef.current = [];
        if (session) {
            session.recordedChunks = recordedChunksRef.current;
            session.mediaStream = resources.recorderStream;
            session.additionalStreams = resources.additionalStreams;
            session.audioContext = resources.audioContext;
            session.cleanupCallbacks = cleanupCallbacks;
            session.displayStreamCleanup = null;
            session.mixerControl = resources.mixerControl || null;
            session.systemStreamLabel = resources.systemStreamLabel || null;
        }

        if (resources.displayStream) {
            const displayStreamCleanup = registerDisplayStreamCleanup(resources.displayStream);
            cleanupCallbacks.push(displayStreamCleanup);

            if (session) {
                session.cleanupCallbacks = cleanupCallbacks;
                session.displayStreamCleanup = displayStreamCleanup;
            }

            if (!resources.warningMessage && mode === 'microphone+system') {
                resultingMode = 'microphone+system';
            }
        }

        if (typeof MediaRecorder === 'undefined') {
            throw new Error(t('recorder.errors.recordingNotSupported'));
        }

        // Niet elke browser kan WebM/Opus opnemen (Safari niet), dus vragen we
        // eerst welk formaat hier wél werkt. Levert dat niets op, dan laten we
        // de browser zelf kiezen in plaats van een NotSupportedError te riskeren.
        const preferredMimeType = selectRecorderMimeType();
        const recorderOptions: MediaRecorderOptions = {
            audioBitsPerSecond: RECORDER_AUDIO_BITS_PER_SECOND
        };
        if (preferredMimeType) {
            recorderOptions.mimeType = preferredMimeType;
        }

        const recorder = new MediaRecorder(resources.recorderStream, recorderOptions);

        // recorder.mimeType is het formaat dat de browser echt gebruikt; dat is
        // leidend voor de blob en de bestandsnaam.
        const activeMimeType = recorder.mimeType || preferredMimeType || RECORDER_MIME_TYPE;
        recorderMimeTypeRef.current = activeMimeType;
        if (preferredMimeType !== RECORDER_MIME_TYPE) {
            logger.info('Opnameformaat afwijkend van voorkeur:', { preferredMimeType, activeMimeType });
        }

        mediaRecorderRef.current = recorder;
        if (session) {
            session.mediaRecorder = recorder;
            session.recorderMimeType = activeMimeType;
        }
        setMediaRecorderInstance(recorder);

        if (resources.warningMessage) {
            setUserStatusMessage(resources.warningMessage);
            resultingMode = 'microphone';
        } else if (mode === 'microphone+system') {
            // Geen permanente "systeemaudio is actief"-melding meer: die plek is nu
            // voor de notities. Stoppen van de schermdeling beëindigt de opname nog
            // steeds automatisch (dat blijft elders afgehandeld).
            setUserStatusMessage('');
            resultingMode = 'microphone+system';
        } else {
            setUserStatusMessage('');
            resultingMode = 'microphone';
        }

        recorder.start(RECORDER_TIMESLICE_MS);
        setIsRecording(true);
        const startTime = new Date().toISOString();
        updateRecordingStartTime(startTime);
        startTimer();

        // Initialize incremental saving
        const recordingId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        debugFileId('generated fileId', recordingId);
        assertValidFileId(recordingId, 'src/components/Recorder.tsx recordingId generation');
        currentRecordingIdRef.current = recordingId;

        // Initialize in DB
        initializeRecording(recordingId, {
            id: recordingId,
            fileName: `recording-${startTime}.${getRecorderFileExtension(activeMimeType)}`,
            createdAt: Date.now(),
            recordingStartTime: startTime,
            mimeType: activeMimeType
        }).catch(err => logger.error('Failed to initialize recording in DB:', err));

        if (session) {
            session.activeLiveMode = resultingMode;
            session.recordingId = recordingId;
        }

        // Bewaak de systeemaudio van de gedeelde bron gedurende de opname.
        if (resources.displayStream) {
            startSourceAudioWatch(resources.displayStream);
        }

        return resultingMode;
    };

    const stopLiveRecording = async () => {
        const recorder = mediaRecorderRef.current;
        if (!recorder) {
            return;
        }

        if (recorder.state === 'recording') {
            try {
                recorder.requestData();
            } catch (requestDataError) {
                logger.warn('Kon laatste recorderdata niet flushen:', requestDataError);
            }
        }

        if (recorder.state === 'recording' || recorder.state === 'paused') {
            recorder.stop();
        }

        setIsRecording(false);
        setIsPaused(false);
        stopTimer();
    };


    useEffect(() => {
        const recorder = mediaRecorderInstance;
        if (!recorder) {
            return;
        }

        const handleData = (event: BlobEvent) => {
            if (event.data.size > 0) {
                const session = recorderSessionRef.current;

                // 1. Save to memory (legacy/backup)
                if (session) {
                    session.recordedChunks.push(event.data);
                    recordedChunksRef.current = session.recordedChunks;
                } else {
                    recordedChunksRef.current.push(event.data);
                }

                // 2. Save to DB (Incremental)
                if (currentRecordingIdRef.current) {
                    const chunkIndex = recordedChunksRef.current.length - 1; // 0-based index
                    persistRecordedChunk(currentRecordingIdRef.current, event.data, chunkIndex);
                }
            }
        };

        const handleStop = async () => {
            if (isDiscardingRef.current) {
                // Opname is bewust verwijderd: niets verwerken of uploaden.
                return;
            }

            const session = recorderSessionRef.current;
            setIsProcessingRecording(true);
            setUserStatusMessage(t('recorder.status.processing'));

            try {
                await cleanupSessionMediaResources(session);
                if (session) {
                    const nextActiveMode = session.preferredLiveMode ?? 'microphone';
                    setActiveLiveMode(nextActiveMode);
                }
                await processRecordedAudio();
            } catch (error) {
                logger.error('Error processing recording after stop:', error);
                const message = error instanceof Error ? error.message : t('recorder.errors.processingFailed');
                setErrorMessage(message);
                setUserStatusMessage('');
            } finally {
                if (session) {
                    session.recordingStartTime = null;
                    session.mediaRecorder = null;
                    session.recordedChunks = recordedChunksRef.current;
                    session.recordingId = null;
                    session.mixerControl = null;
                    session.systemStreamLabel = null;
                }
                mediaRecorderRef.current = null;
                setMediaRecorderInstance(null);
                setIsProcessingRecording(false);
            }
        };

        recorder.ondataavailable = handleData;
        recorder.onstop = handleStop;

        return () => {
            if (recorder.ondataavailable === handleData) {
                recorder.ondataavailable = null;
            }
            if (recorder.onstop === handleStop) {
                recorder.onstop = null;
            }
        };
    }, [cleanupSessionMediaResources, mediaRecorderInstance, persistRecordedChunk, processRecordedAudio, t]);


    // Een nieuwe verbinding maakt het pad weer vrij voor één nieuwe auto-poging
    // per pending-id (zie de guard hieronder).
    useEffect(() => {
        const handleOnline = () => {
            autoResumeMicAttemptedIdRef.current = null;
            autoResumeFileAttemptedIdRef.current = null;
        };
        window.addEventListener('online', handleOnline);
        return () => window.removeEventListener('online', handleOnline);
    }, []);

    useEffect(() => {
        if (!pendingMicrophoneRecovery || isUploading) {
            return;
        }

        if (!navigator.onLine) {
            setUserStatusMessage(t('recorder.status.waitingForNetworkRecording'));
            return;
        }

        // Voorkom een retry-storm: zodra de upload mislukt zet
        // resumePendingMicrophoneUpload isUploading terug op false terwijl de
        // pending-opname blijft staan, waardoor dit effect direct opnieuw zou
        // vuren. Probeer daarom per opname-id hooguit één keer automatisch te
        // hervatten; bij een niet-herhaalbare fout laat de gebruiker het via de
        // "Upload hervatten"-knop opnieuw proberen. Een online-event (zie boven)
        // maakt het pad weer vrij.
        if (autoResumeMicAttemptedIdRef.current === pendingMicrophoneRecovery.id) {
            return;
        }
        autoResumeMicAttemptedIdRef.current = pendingMicrophoneRecovery.id;
        resumePendingMicrophoneUpload();
    }, [isUploading, pendingMicrophoneRecovery, resumePendingMicrophoneUpload, t]);

    useEffect(() => {
        if (!pendingFileRecovery || pendingMicrophoneRecovery || isUploading) {
            return;
        }

        if (!navigator.onLine) {
            setUserStatusMessage(t('recorder.status.waitingForNetworkFile'));
            return;
        }

        // Zelfde retry-storm-guard als bij de microfoon-opname hierboven.
        if (autoResumeFileAttemptedIdRef.current === pendingFileRecovery.id) {
            return;
        }
        autoResumeFileAttemptedIdRef.current = pendingFileRecovery.id;
        resumePendingFileUpload();
    }, [isUploading, pendingFileRecovery, pendingMicrophoneRecovery, resumePendingFileUpload, t]);



    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        await processFileUpload(file);

        // Reset het file input
        event.target.value = '';
    };

    const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragEnter = (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        event.stopPropagation();

        // Alleen isDragOver op false zetten als we de drop zone verlaten, niet child elementen
        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX;
        const y = event.clientY;

        if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            setIsDragOver(false);
        }
    };

    const handleDrop = async (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);

        if (!user || isUploading || isProcessingRecording) return;

        const files = Array.from(event.dataTransfer.files);
        const file = files[0]; // Neem alleen het eerste bestand

        if (file) {
            await processFileUpload(file);
        }
    };

    const pendingRecovery = pendingMicrophoneRecovery ?? pendingFileRecovery;
    const pendingRecoveryProgress = pendingRecovery && pendingRecovery.totalChunks > 0
        ? Math.round((pendingRecovery.uploadedChunks / pendingRecovery.totalChunks) * 100)
        : 0;
    // Een weggeklikte herstelmelding blijft verborgen; een nieuwe melding of een
    // lopende upload toont het blok weer.
    const isRecoveryNoticeDismissed = Boolean(
        pendingRecovery
        && pendingRecovery.id === dismissedRecoveryId
        && !userStatusMessage
        && !extensionStatusMessage
        && !isUploading
    );



    // Auto-expand when recording/uploading starts (alleen voor floating variant)
    React.useEffect(() => {
        if (isFloatingVariant) {
            if (!isBusy) {
                setIsExpanded(false);
            } else {
                setIsExpanded(true);
            }
        }
        // Inline variant is altijd expanded, dus hier niets doen
    }, [isBusy, isFloatingVariant]);

    const showStartStage = !isRecording && !isPaused && !isProcessingRecording
        && !(isUploading && !isUploadComplete) && !isUploadComplete && recordingType !== 'upload';
    const showUploadZone = recordingType === 'upload' && !isRecording && !isPaused && !isProcessingRecording;
    const showPostRecordingSurface = isProcessingRecording || (isUploading && !isUploadComplete) || isUploadComplete;
    const postRecordingState = isUploadComplete ? 'uploaded' : isUploading ? 'uploading' : 'processing';
    const uploadSurfaceTitle = isUploadComplete
        ? t('recorder.uploadSurface.titleComplete')
        : isUploading
            ? t('recorder.uploadSurface.titleUploading')
            : t('recorder.uploadSurface.titleProcessing');
    const uploadSurfaceStatus = isUploadComplete
        ? t('recorder.uploadSurface.statusComplete')
        : isUploading
            ? (statusMessage || t('recorder.uploadSurface.statusUploading'))
            : t('recorder.uploadSurface.statusProcessing');
    const uploadSurfaceProgress = isUploadComplete ? 100 : isUploading ? uploadProgress : 0;
    const handleStartClick = () => {
        // Geen systeemaudio mogelijk in deze browser: direct met de microfoon
        // starten, een keuze tonen heeft dan geen zin.
        if (!systemAudioSupport.available) {
            void startRecordingWithMode('microphone');
            return;
        }

        setIsChoosingMode(true);
    };

    const handleChooseMicrophone = () => {
        setIsChoosingMode(false);
        void startRecordingWithMode('microphone');
    };

    const handleChooseMeeting = () => {
        setIsChoosingMode(false);
        setPreferredLiveMode('microphone+system');
        setIsLiveModeDialogOpen(true);
        setWizardStep('instruction');
    };

    const resizeNotesTextarea = () => {
        const el = notesTextareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    };

    // Uitklapbaar notitieveld; getoond op het startscherm (voorbereiding) en
    // tijdens de opname. De tekst wordt bij upload als ondersteunend document
    // gekoppeld (zie attachMeetingNotesToSupportingDocuments).
    const renderNotesSection = () => (
        <section className="recorder-oats-notes-shell">
            <button
                type="button"
                className="recorder-oats-transcript-toggle"
                onClick={() => setIsNotesExpanded((expanded) => !expanded)}
                aria-expanded={isNotesExpanded}
            >
                <span className="recorder-oats-transcript-title">{t('recorder.notes.title')}</span>
                {meetingNotesDraft.length > 0 && (
                    <span className="recorder-oats-transcript-meta">
                        {t('recorder.notes.charsCount', { count: meetingNotesDraft.length })}
                    </span>
                )}
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={isNotesExpanded ? 'is-expanded' : undefined}
                >
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>

            {isNotesExpanded && (
                <section className="recorder-oats-notes-panel">
                    <textarea
                        ref={(el) => {
                            notesTextareaRef.current = el;
                            if (el) resizeNotesTextarea();
                        }}
                        className="recorder-notes-input"
                        value={meetingNotesDraft}
                        maxLength={MEETING_NOTES_MAX_CHARS}
                        placeholder={t('recorder.notes.placeholder')}
                        onChange={(event) => {
                            setMeetingNotesDraft(event.target.value);
                            resizeNotesTextarea();
                        }}
                    />
                    <div className="recorder-notes-controls">
                        <span className="recorder-notes-count">
                            {meetingNotesDraft.length}/{MEETING_NOTES_MAX_CHARS}
                        </span>
                    </div>
                </section>
            )}
        </section>
    );

    return (
        <>
            {canShowContainer && (
                <section
                    id="recorder-container"
                    ref={recorderRootRef}
                    className={`recorder-card recorder-oats-shell ${className}`}
                >
                    {/* Topbar: status + audiobron-instelling */}
                    <header className="recorder-oats-topbar">
                        <div className="recorder-oats-brand">
                            <div className="recorder-oats-brand-row">
                                <span className={`recorder-status ${isRecording ? 'recording' : isPaused ? 'paused' : 'idle'}`}>
                                    <span className="status-dot" />
                                    <span>{statusLabel}</span>
                                </span>
                            </div>
                        </div>
                    </header>

                    {/* Status-/herstelmelding */}
                    {statusMessage && !showPostRecordingSurface && !isRecoveryNoticeDismissed && (
                        <div className="recorder-oats-notice">
                            <div className="rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-700 shadow-sm">
                                <div className="flex items-start gap-3">
                                    <div className="min-w-0 flex-1">{statusMessage}</div>
                                    <button
                                        type="button"
                                        onClick={dismissStatusMessage}
                                        aria-label={t('recorder.notice.dismiss')}
                                        className="-mr-1 -mt-1 flex-shrink-0 rounded-md p-1 text-primary-400 transition-colors hover:bg-primary-100 hover:text-primary-700"
                                    >
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                                {pendingRecovery && (
                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={pendingMicrophoneRecovery ? resumePendingMicrophoneUpload : resumePendingFileUpload}
                                            disabled={isUploading}
                                            className="inline-flex items-center rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {t('recorder.notice.resumeUpload')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={requestDiscardPendingRecovery}
                                            className="inline-flex items-center rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                                        >
                                            {isConfirmingRecoveryDelete ? t('recorder.notice.confirmDelete') : t('recorder.notice.delete')}
                                        </button>
                                        <span className="text-xs text-primary-600">
                                            {t('recorder.notice.completedPercent', { progress: pendingRecoveryProgress })}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Foutmelding */}
                    {visibleErrorMessage && (
                        <div className="recorder-oats-notice">
                            <div className="rounded-xl border border-red-200/60 bg-red-50 px-4 py-3 shadow-sm">
                                <div className="flex items-start gap-3">
                                    <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-red-800">{t('recorder.errorNotice.title')}</p>
                                        <p className="mt-0.5 text-sm text-red-700">{visibleErrorMessage}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={dismissError}
                                        aria-label={t('recorder.errorNotice.dismiss')}
                                        className="-mr-1 -mt-1 flex-shrink-0 rounded-md p-1 text-red-400 transition-colors hover:bg-red-100 hover:text-red-700"
                                    >
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Bronwaarschuwing tijdens opname */}
                    {recordingSourceWarning && (isRecording || isPaused) && (
                        <div className="recorder-oats-notice">
                            <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 shadow-sm">
                                <div className="flex items-start gap-3">
                                    <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-amber-900">{t('recorder.sourceWarning.title')}</p>
                                        <p className="mt-0.5 text-xs leading-relaxed text-amber-700">{recordingSourceWarning}</p>
                                        <div className="mt-2 flex items-center gap-2">
                                            <button
                                                onClick={handleSwitchScreen}
                                                className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors duration-150 hover:bg-amber-700"
                                            >
                                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                {t('recorder.sourceWarning.switchSource')}
                                            </button>
                                            <button
                                                onClick={() => setRecordingSourceWarning(null)}
                                                className="text-xs text-amber-600 transition-colors duration-150 hover:text-amber-800"
                                            >
                                                {t('recorder.sourceWarning.ignore')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Afspraak uit Start Notizy: gaat mee met deze opname, tenzij weggeklikt */}
                    {partnerRecordingTarget && (showStartStage || showUploadZone || isRecording || isPaused) && (
                        <div className="recorder-oats-notice">
                            <div className="flex items-center gap-3 rounded-xl border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-800">
                                <svg className="h-4 w-4 flex-shrink-0 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="min-w-0 flex-1 truncate">
                                    {partnerRecordingTarget.context.label
                                        ? t('recorder.partnerTarget.label', { afspraak: partnerRecordingTarget.context.label })
                                        : t('recorder.partnerTarget.labelGeneric', { partner: partnerRecordingTarget.partnerName })}
                                </span>
                                <button
                                    type="button"
                                    onClick={clearPartnerRecordingTarget}
                                    aria-label={t('recorder.partnerTarget.dismiss')}
                                    title={t('recorder.partnerTarget.dismiss')}
                                    className="-mr-1 flex-shrink-0 rounded-md p-1 text-primary-400 transition-colors hover:bg-primary-100 hover:text-primary-700"
                                >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Idle: grote startknop */}
                    {showStartStage && !isChoosingMode && (
                        <>
                            <section className="recorder-oats-start-stage">
                                <button
                                    type="button"
                                    className="recorder-oats-start-button"
                                    onClick={handleStartClick}
                                    disabled={!user || isStartingRecording || isUploading || isProcessingRecording}
                                >
                                    <span className="recorder-oats-start-icon">
                                        {isStartingRecording ? (
                                            <svg className="h-7 w-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" stroke="none" />
                                                <path d="M5 11a7 7 0 0 0 14 0" />
                                                <line x1="12" y1="18" x2="12" y2="22" />
                                                <line x1="9" y1="22" x2="15" y2="22" />
                                            </svg>
                                        )}
                                    </span>
                                    <span className="recorder-oats-start-label">
                                        {isStartingRecording ? t('recorder.start.starting') : t('recorder.start.startRecording')}
                                    </span>
                                </button>
                            </section>
                            <div className="recorder-oats-secondary-row">
                                <button
                                    type="button"
                                    className="recorder-oats-upload-link"
                                    onClick={() => setRecordingType('upload')}
                                    disabled={!user || isUploading || isStartingRecording || isProcessingRecording}
                                >
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12" />
                                    </svg>
                                    <span>{t('recorder.start.uploadExisting')}</span>
                                </button>
                            </div>
                            {user && renderNotesSection()}
                        </>
                    )}

                    {/* Keuze: microfoon of digitale meeting */}
                    {showStartStage && isChoosingMode && (
                        <section className="recorder-oats-mode-stage">
                            <p className="recorder-oats-mode-title">{t('recorder.modeStage.question')}</p>
                            <div className="recorder-oats-mode-grid">
                                <button
                                    type="button"
                                    className="recorder-oats-mode-option"
                                    onClick={handleChooseMicrophone}
                                    disabled={!user || isStartingRecording}
                                >
                                    <span className="recorder-oats-mode-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 3a3 3 0 013 3v5a3 3 0 01-6 0V6a3 3 0 013-3zm6 8a6 6 0 01-12 0m6 6v4m-3 0h6" />
                                        </svg>
                                    </span>
                                    <span className="recorder-oats-mode-label">{t('recorder.modeStage.microphone')}</span>
                                    <span className="recorder-oats-mode-desc">{t('recorder.modeStage.microphoneDesc')}</span>
                                </button>
                                <button
                                    type="button"
                                    className="recorder-oats-mode-option"
                                    onClick={handleChooseMeeting}
                                    disabled={!user || isStartingRecording || !systemAudioSupport.available}
                                >
                                    <span className="recorder-oats-mode-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                    </span>
                                    <span className="recorder-oats-mode-label">{t('recorder.modeStage.meeting')}</span>
                                    <span className="recorder-oats-mode-desc">
                                        {systemAudioSupport.available
                                            ? t('recorder.modeStage.meetingDescAvailable')
                                            : (systemAudioSupport.reason || t('recorder.modeStage.meetingDescUnavailable'))}
                                    </span>
                                </button>
                            </div>
                            <button
                                type="button"
                                className="recorder-oats-upload-link"
                                onClick={() => setIsChoosingMode(false)}
                            >
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span>{t('recorder.modeStage.cancel')}</span>
                            </button>
                        </section>
                    )}

                    {/* Bestand uploaden (dropzone) */}
                    {showUploadZone && (
                        <div className="recorder-oats-notice" style={{ marginBottom: 22 }}>
                            <input
                                type="file"
                                accept=".mp3,.wav,.m4a,.webm,.ogg,.aac,audio/*"
                                onChange={handleFileUpload}
                                disabled={!user || isUploading || isProcessingRecording}
                                className="hidden"
                                id="audio-file-input"
                            />
                            <label
                                htmlFor="audio-file-input"
                                className={`
                            group block w-full border-2 border-dashed rounded-xl text-center transition-all duration-300 cursor-pointer
                            ${!user || isUploading || isProcessingRecording
                                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                                        : isDragOver
                                            ? 'border-green-400 bg-green-50 p-8 shadow-md'
                                            : 'border-gray-300 bg-white/60 hover:border-primary-300 hover:bg-primary-50/60 p-6'
                                    }
                        `}
                                onDragOver={handleDragOver}
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <div className={`space-y-3 ${isDragOver ? 'space-y-4' : ''} transition-all duration-300`}>
                                    <div className={`mx-auto h-16 w-16 rounded-full ${isUploading
                                        ? 'bg-green-100 text-green-600'
                                        : isDragOver
                                            ? 'scale-110 bg-green-500 text-white'
                                            : 'bg-gray-200 text-gray-600 group-hover:bg-primary-100 group-hover:text-primary-700'
                                        } flex items-center justify-center transition-all duration-300`}>
                                        {isUploading ? (
                                            <svg className="h-6 w-6 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                            </svg>
                                        )}
                                    </div>
                                    <div>
                                        <div className={`font-semibold transition-all duration-300 ${isDragOver ? 'text-lg text-green-700' : 'text-base text-gray-800 group-hover:text-primary-700'}`}>
                                            {isUploading
                                                ? t('recorder.uploadZone.uploading')
                                                : isDragOver
                                                    ? t('recorder.uploadZone.dragActive')
                                                    : t('recorder.uploadZone.idle')
                                            }
                                        </div>
                                        <div className={`text-gray-500 transition-all duration-300 ${isDragOver ? 'text-sm' : 'text-xs'}`}>
                                            {isUploading
                                                ? t('recorder.uploadZone.uploadingSub')
                                                : isDragOver
                                                    ? t('recorder.uploadZone.dragActiveSub')
                                                    : t('recorder.uploadZone.idleSub')
                                            }
                                        </div>
                                    </div>
                                    {(isDragOver || isUploading) && (
                                        <div className="inline-block rounded-lg bg-white/50 px-3 py-1 text-xs text-gray-400">
                                            {t('recorder.uploadZone.formats')}
                                        </div>
                                    )}
                                </div>
                            </label>
                            <div className="recorder-oats-secondary-row" style={{ paddingBottom: 0 }}>
                                <button
                                    type="button"
                                    className="recorder-oats-upload-link"
                                    onClick={() => setRecordingType('microphone')}
                                >
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    <span>{t('recorder.uploadZone.backToRecording')}</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Post-recording / upload-surface */}
                    {showPostRecordingSurface && (
                        <div
                            className={`recorder-post-recording recorder-post-recording-inline state-${postRecordingState}`}
                            role="status"
                            aria-live="polite"
                        >
                            <div className="recorder-post-recording-header">
                                <div className="recorder-post-recording-copy">
                                    <p className="recorder-post-recording-title">{uploadSurfaceTitle}</p>
                                </div>
                                {postRecordingState !== 'processing' && (
                                    <span className="recorder-post-recording-percent">{uploadSurfaceProgress}%</span>
                                )}
                            </div>
                            <div className="recorder-post-recording-progress">
                                <div className="recorder-upload-track recorder-post-recording-track">
                                    <div className="recorder-upload-fill" style={{ width: `${uploadSurfaceProgress}%` }} />
                                </div>
                            </div>
                            <p className="recorder-post-recording-status">{uploadSurfaceStatus}</p>
                        </div>
                    )}

                    {/* Inloggen vereist */}
                    {!user && (
                        <div className="recorder-oats-notice" style={{ marginBottom: 22 }}>
                            <div className="rounded-xl border border-amber-200/60 bg-amber-50 px-4 py-3 shadow-sm">
                                <p className="text-sm font-semibold text-amber-800">{t('recorder.loginRequired.title')}</p>
                                <p className="mt-0.5 text-sm text-amber-700">{t('recorder.loginRequired.body')}</p>
                            </div>
                        </div>
                    )}

                    {/* Notities tijdens de opname (op de plek van de oude
                        systeemaudio-melding) */}
                    {(isRecording || isPaused) && renderNotesSection()}

                    {/* Controlbar tijdens opname/pauze */}
                    {(isRecording || isPaused) && (
                        <footer className="recorder-oats-controlbar">
                            <div className="recorder-oats-control-left">
                                <button
                                    type="button"
                                    className="recorder-oats-action-primary"
                                    onClick={() => void (isPaused ? handleResumeRecording() : handlePauseRecording())}
                                    aria-label={isPaused ? t('recorder.controlbar.resume') : t('recorder.controlbar.pause')}
                                    title={isPaused ? t('recorder.controlbar.resumeTitle') : t('recorder.controlbar.pauseTitle')}
                                >
                                    {isPaused ? (
                                        <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="6" /></svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <rect x="6" y="5" width="4" height="14" rx="1" />
                                            <rect x="14" y="5" width="4" height="14" rx="1" />
                                        </svg>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className={`recorder-oats-action is-strong${!isPaused ? ' is-hidden' : ''}`}
                                    onClick={() => void handleFinishRecording()}
                                    aria-label={t('recorder.controlbar.save')}
                                    data-tooltip={t('recorder.controlbar.saveTooltip')}
                                >
                                    <span className="recorder-oats-verwerk-label">{t('recorder.controlbar.save')}</span>
                                    <span className="recorder-oats-verwerk-confirm" aria-hidden="true">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M5 12l5 5L20 7" />
                                        </svg>
                                    </span>
                                </button>
                                <OatsMeter
                                    stream={recorderSessionRef.current.mediaStream ?? mediaRecorderInstance?.stream ?? null}
                                    isPaused={isPaused}
                                />
                            </div>
                            <div className="recorder-oats-control-right">
                                <span className="recorder-oats-timer">{formatTime(recordingTime)}</span>
                                <button
                                    type="button"
                                    className="recorder-oats-action-icon"
                                    onClick={handleSwitchScreen}
                                    aria-label={activeLiveMode === 'microphone+system' ? t('recorder.controlbar.switchScreen') : t('recorder.controlbar.addScreen')}
                                    data-tooltip={activeLiveMode === 'microphone+system' ? t('recorder.controlbar.switchScreenTooltip') : t('recorder.controlbar.addScreenTooltip')}
                                >
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    className={`recorder-oats-action-icon is-danger recorder-oats-discard-toggle${isConfirmingDiscard ? ' is-confirming' : ''}`}
                                    onClick={requestDiscardRecording}
                                    aria-label={isConfirmingDiscard ? t('recorder.controlbar.confirmDelete') : t('recorder.controlbar.deleteRecording')}
                                    data-tooltip={isConfirmingDiscard ? t('recorder.controlbar.confirmDeleteTooltip') : t('recorder.controlbar.deleteTooltip')}
                                >
                                    <svg className="recorder-oats-discard-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" />
                                    </svg>
                                    <span className="recorder-oats-discard-label" aria-hidden="true">{t('recorder.controlbar.sure')}</span>
                                </button>
                            </div>
                        </footer>
                    )}
                </section>
            )}
            {renderFloatingIndicator()}
            {isLiveModeDialogOpen && createPortal(
                <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-gray-900/60 px-4 py-6 backdrop-blur-sm">
                    <div className="w-full max-w-2xl rounded-2xl bg-white p-0 shadow-2xl ring-1 ring-gray-900/5 overflow-hidden flex flex-col max-h-[90vh]">
                        {/* Wizard Header */}
                        <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between bg-gray-50/50">
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">
                                    {wizardStep === 'instruction' && t('recorder.wizard.instructionTitle')}
                                    {wizardStep === 'verification' && t('recorder.wizard.verificationTitle')}
                                </h2>
                                <p className="text-sm text-gray-500">
                                    {wizardStep === 'instruction' && t('recorder.wizard.instructionSubtitle')}
                                    {wizardStep === 'verification' && t('recorder.wizard.verificationSubtitle')}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsLiveModeDialogOpen(false);
                                    setWizardStep('mode-selection');
                                    if (previewStreams) {
                                        if (previewStreams.mic) stopStreamTracks(previewStreams.mic);
                                        if (previewStreams.system) stopStreamTracks(previewStreams.system);
                                        setPreviewStreams(null);
                                        setVerificationWarning(null);
                                    }
                                }}
                                className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                            >
                                <span className="sr-only">{t('recorder.wizard.close')}</span>
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Wizard Content */}
                        <div className="p-6 overflow-y-auto">
                            {/* Fouten uit het verkrijgen van de scherm-/microfoonstream (bv. een
                                door de browser geweigerde constraint of geblokkeerde schermdeling)
                                tonen we hier bovenaan; de melding in de hoofdkaart valt anders achter
                                deze dialog weg en de gebruiker ziet "er gebeurt niets". */}
                            {visibleErrorMessage && (
                                <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex gap-3">
                                    <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <div>
                                        <p className="text-sm font-semibold text-red-900">{t('recorder.wizard.screenSharingNotStarted')}</p>
                                        <p className="text-sm text-red-700 mt-0.5 leading-relaxed">{visibleErrorMessage}</p>
                                    </div>
                                </div>
                            )}
                            {wizardStep === 'instruction' && (
                                <div className="space-y-6">
                                    <div className="rounded-xl bg-primary-50 border border-primary-100 p-5">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-lg">
                                                !
                                            </div>
                                            <div className="space-y-3">
                                                <h3 className="font-bold text-primary-900">{t('recorder.wizard.chooseSource')}</h3>
                                                {systemAudioSupport.screenAudio ? (
                                                    <>
                                                        <p className="text-sm text-primary-800 leading-relaxed">
                                                            <Trans i18nKey="recorder.wizard.instructionScreenAudio1" components={{ strong: <strong /> }} />
                                                        </p>
                                                        <p className="text-sm text-primary-800 leading-relaxed">
                                                            <Trans i18nKey="recorder.wizard.instructionScreenAudio2" components={{ strong: <strong /> }} />
                                                        </p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p className="text-sm text-primary-800 leading-relaxed">
                                                            <Trans i18nKey="recorder.wizard.instructionTabAudio1" components={{ strong: <strong /> }} />
                                                        </p>
                                                        <p className="text-sm text-primary-800 leading-relaxed">
                                                            <Trans i18nKey="recorder.wizard.instructionTabAudio2" components={{ strong: <strong /> }} />
                                                        </p>
                                                    </>
                                                )}
                                                <div className="bg-white rounded-lg border border-primary-200 p-3 shadow-sm">
                                                    <div className="flex items-center gap-3 text-sm text-gray-600">
                                                        <div className="w-4 h-4 border-2 border-primary-600 rounded bg-primary-600 flex items-center justify-center">
                                                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                                        </div>
                                                        <span className="font-medium text-gray-900">{systemAudioSupport.screenAudio ? t('recorder.wizard.shareSystemAudioCheckbox') : t('recorder.wizard.shareTabAudioCheckbox')}</span>
                                                        <span className="text-xs text-gray-400">{t('recorder.wizard.checkboxHint')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {wirelessOutputDetected && systemAudioSupport.screenAudio && (
                                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
                                            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                            </svg>
                                            <div>
                                                <p className="text-sm font-semibold text-amber-900">{t('recorder.wizard.wirelessTitle')}</p>
                                                <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                                                    <Trans i18nKey="recorder.wizard.wirelessBody" components={{ strong: <strong /> }} />
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between pt-2">
                                        <button
                                            onClick={() => {
                                                setIsLiveModeDialogOpen(false);
                                                setWizardStep('mode-selection');
                                                setIsChoosingMode(true);
                                            }}
                                            className="text-gray-500 hover:text-gray-700 font-medium text-sm px-4 py-2"
                                        >
                                            {t('recorder.wizard.back')}
                                        </button>
                                        <button
                                            onClick={acquireStreamsAndStartRecording}
                                            className="bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-semibold shadow-lg shadow-emerald-200 hover:bg-emerald-700 hover:shadow-emerald-300 transition-all flex items-center gap-2"
                                        >
                                            <span>{systemAudioSupport.screenAudio ? t('recorder.wizard.selectScreen') : t('recorder.wizard.selectTab')}</span>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {wizardStep === 'verification' && previewStreams && (
                                <div className="space-y-8">
                                    {previewSelectionWarning && !verificationError && !verificationWarning && (
                                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex gap-3">
                                            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-7.938 4h15.876c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L2.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                            </svg>
                                            <div>
                                                <p className="text-sm font-medium text-amber-900">{t('recorder.wizard.selectionWarningTitle')}</p>
                                                <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                                                    {previewSelectionWarning}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {verificationWarning && !verificationError && (
                                        <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 px-5 py-4 flex gap-4 shadow-sm">
                                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-amber-900">{t('recorder.wizard.noSoundTitle')}</p>
                                                <p className="text-sm text-amber-800 mt-1 leading-relaxed">
                                                    {verificationWarning}
                                                </p>
                                                <p className="text-xs text-amber-600 mt-2">
                                                    {t('recorder.wizard.noSoundHint')}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid gap-6 sm:grid-cols-2">
                                        {/* Mic Visualizer */}
                                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="p-1.5 bg-white rounded-lg shadow-sm text-gray-600">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
                                                    </div>
                                                    <span className="font-semibold text-gray-700">{t('recorder.wizard.yourMicrophone')}</span>
                                                </div>
                                            </div>
                                            <AudioVisualizer stream={previewStreams.mic || null} color="black" />
                                            <p className="text-xs text-gray-500 mt-2 text-center">{t('recorder.wizard.saySomething')}</p>
                                        </div>

                                        {/* System Visualizer */}
                                        <div
                                            className={`bg-gray-50 rounded-xl p-4 border ${verificationError ? 'border-red-300 bg-red-50 cursor-pointer hover:bg-red-100 transition-colors' : 'border-gray-200'}`}
                                            onClick={verificationError ? () => {
                                                // Cleanup current streams and go back to instruction to retry
                                                if (previewStreams?.mic) stopStreamTracks(previewStreams.mic);
                                                if (previewStreams?.system) stopStreamTracks(previewStreams.system);
                                                setPreviewStreams(null);
                                                setVerificationError(null);
                                                setVerificationWarning(null);
                                                setWizardStep('instruction');
                                            } : undefined}
                                            role={verificationError ? 'button' : undefined}
                                            tabIndex={verificationError ? 0 : undefined}
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <div className={`p-1.5 rounded-lg shadow-sm ${verificationError ? 'bg-red-100 text-red-600' : 'bg-white text-gray-600'}`}>
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                                    </div>
                                                    <span className={`font-semibold ${verificationError ? 'text-red-700' : 'text-gray-700'}`}>{t('recorder.wizard.systemAudio')}</span>
                                                </div>
                                                {verificationError && (
                                                    <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{t('recorder.wizard.errorBadge')}</span>
                                                )}
                                            </div>
                                            {verificationError ? (
                                                <div className="h-12 flex flex-col items-center justify-center text-center gap-1">
                                                    <p className="text-xs text-red-600 font-medium">{verificationError}</p>
                                                    <p className="text-xs text-red-500 underline">{t('recorder.wizard.clickToReselect')}</p>
                                                </div>
                                            ) : (
                                                <>
                                                    <AudioVisualizer stream={previewStreams.system || null} color="black" />
                                                    <p className="text-xs text-gray-500 mt-2 text-center">{t('recorder.wizard.playSoundInSource')}</p>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {verificationError && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                                            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                                            <div>
                                                <p className="text-sm font-medium text-amber-800">{t('recorder.wizard.noSystemAudioQuestion')}</p>
                                                <p className="text-xs text-amber-700 mt-1">
                                                    {systemAudioSupport.screenAudio
                                                        ? t('recorder.wizard.noSystemAudioHintScreenAudio')
                                                        : t('recorder.wizard.noSystemAudioHintTabAudio')}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                        <button
                                            onClick={() => {
                                                const shouldRetry = !!verificationError || !!verificationWarning;
                                                // Cleanup previews
                                                if (previewStreams.mic) stopStreamTracks(previewStreams.mic);
                                                if (previewStreams.system) stopStreamTracks(previewStreams.system);
                                                setPreviewStreams(null);
                                                setVerificationError(null);
                                                setVerificationWarning(null);
                                                setWizardStep('instruction'); // Annuleert de gebruiker de kiezer, dan landt hij hier
                                                if (shouldRetry) {
                                                    // Direct de kiezer opnieuw openen: een vergeten audio-vinkje kost zo maar één klik
                                                    void acquireStreamsAndStartRecording();
                                                }
                                            }}
                                            className="text-gray-500 hover:text-gray-700 font-medium text-sm px-4 py-2"
                                        >
                                            {verificationError ? t('recorder.wizard.retry') : verificationWarning ? t('recorder.wizard.chooseOtherSource') : t('recorder.wizard.back')}
                                        </button>
                                        <button
                                            disabled={!!verificationError}
                                            onClick={async () => {
                                                // Start actual recording using the PREVIEW streams!
                                                // We need to modify startLiveRecording to accept existing streams or handle this logic

                                                // HACK: We'll close the preview streams and let startLiveRecording re-acquire them? 
                                                // NO, that's bad UX (asking twice).
                                                // We should pass these streams to startLiveRecording.

                                                setIsLiveModeDialogOpen(false);
                                                setWizardStep('mode-selection');

                                                try {
                                                    await startLiveRecording('microphone+system', previewStreams);
                                                    setPreviewStreams(null); // Ownership transferred
                                                } catch (err) {
                                                    logger.error("Failed to start from wizard", err);
                                                    setErrorMessage(t('recorder.errors.startFromWizardFailed'));
                                                }
                                            }}
                                            className={`px-8 py-3 rounded-xl font-bold shadow-lg transition-all flex items-center gap-2 ${verificationError
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-red-600 text-white shadow-red-200 hover:bg-red-700 hover:shadow-red-300 hover:scale-105'
                                                }`}
                                        >
                                            <div className={`w-3 h-3 rounded-full ${verificationError ? 'bg-gray-300' : 'bg-white animate-pulse'}`}></div>
                                            <span>{t('recorder.wizard.startRecording')}</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default Recorder;
